'use strict';

const libPictView = require('pict-view');
const libFrameworkClient = require('../providers/FrameworkClient.js');
const libDescriptors = require('../EntityDescriptors.js');
const libLabels = require('../libLabels.js');
const libShareUI = require('../BlueprintShareUI.js');
const libFocus = require('../FocusFilters.js');
const libPromptEmbed = require('../PromptEmbed.js');
const libMarkdownRender = require('./MarkdownRender.js');
const libCandidateSearch = require('../CandidateSearch.js');

function _escapeHTML(pValue)
{
	return String(pValue == null ? '' : pValue)
		.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/**
 * EntityDetail — the Phase-2 connective-tissue surface. showDetail(key, id)
 * renders one record's fields plus its relationships, driven by the entity's
 * `Relations` descriptor:
 *   - fk            single dropdown that sets a typed FK on the record (Feature→Module)
 *   - link          add/remove list backed by a join table (Feature↔Vision, Vision↔Vision)
 *   - inverse-fk    read-only list of records pointing here by FK (Module's Features, WorkItems about this)
 *   - inverse-join  read-only list of records linked here through a join (Vision's Features)
 *   - target        WorkItem "about": a type picker + an entity picker (typed nullable FK)
 *
 * Relationship targets are resolved from the entity providers' caches, so the
 * view loads all four entity lists + this record's join rows before rendering.
 * Linked chips are clickable to walk the graph. Tenancy is server-side.
 */
class PictViewEntityDetail extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		this._key = '';
		this._id = 0;
		this._linkRows = {};
		this._pending = {};
		this._targetType = null;
		// The rows resolve() fetched for each loaded entity key, for this view's own pickers to build
		// options from. Deliberately NOT the providers' shared store().List (WI #447) -- that belongs
		// to whichever list screen is paged to it, and this view opening in front of it must not move
		// that page.
		this._entityRows = {};
		this.pict.AppData.EntityDetail = this.pict.AppData.EntityDetail ||
			{ Key: '', HeaderTitle: '', BackLabel: '', Fields: [], Sections: [], AddressToggle: [], AddressedBadge: [] };
	}

	_fmt(pValue) { return (pValue === null || typeof pValue === 'undefined') ? '' : String(pValue); }
	_provider(pKey) { return this.pict.providers[pKey + 'API']; }
	// The rows resolve() fetched for pKey during this load, for a picker to build its options from.
	// NOT store().List (WI #447) -- see the note by _entityRows.
	_rows(pKey) { return this._entityRows[pKey] || []; }

	// A chip's name, resolved out of the target entity's Index. _resolveChipLabels() has already
	// fetched, by id, every linked record this load found missing from it (WI #448), so a miss here
	// means the record genuinely could not be read (deleted, wrong tenant) rather than simply never
	// fetched -- falling back to '#id' in that case is correct and stays.
	_label(pTargetKey, pID)
	{
		let tmpDesc = libDescriptors.Entities[pTargetKey];
		let tmpProvider = this._provider(pTargetKey);
		let tmpRow = tmpProvider && tmpProvider.store().Index[pID];
		return tmpRow ? (tmpRow[tmpDesc.TitleField] || ('#' + pID)) : ('#' + pID);
	}

	// Fetch, by id, exactly the linked records _label() would otherwise have fallen back to '#id' for:
	// a relation chip (link / inverse-join) whose target sits outside the capped page resolve() already
	// loaded for that entity (WI #448). This is the second-order half of WI #433/#445/#446 -- those fixed
	// what a PICKER can offer; an already-linked chip still needed its own name resolved. A single-record
	// read per missing id, not a wider list: cacheRecord() (WI #433) writes the target's Index only, the
	// same seam _label() reads, so this never disturbs a list screen paged to that entity. A record that
	// truly cannot be read (read() resolves null) is left unresolved; _label()'s '#id' fallback covers it.
	_resolveChipLabels(pRelations)
	{
		let tmpNeeded = {};
		let fNote = (pKey, pID) =>
		{
			if (!pKey || !pID || !libDescriptors.Entities[pKey]) { return; }
			let tmpProvider = this._provider(pKey);
			if (!tmpProvider || tmpProvider.store().Index[pID]) { return; }
			(tmpNeeded[pKey] = tmpNeeded[pKey] || {})[pID] = true;
		};
		pRelations.forEach((pRel, pIdx) =>
		{
			let tmpRows = this._linkRows[pIdx] || [];
			if (pRel.Render === 'link') { tmpRows.forEach((pR) => fNote(pRel.Target, pR[pRel.TargetField])); }
			else if (pRel.Render === 'inverse-join') { tmpRows.forEach((pR) => fNote(pRel.Target, pR[pRel.MapField])); }
		});
		let tmpReads = [];
		Object.keys(tmpNeeded).forEach((pKey) =>
		{
			let tmpProvider = this._provider(pKey);
			Object.keys(tmpNeeded[pKey]).forEach((pID) =>
			{
				tmpReads.push(tmpProvider.read(pID).then((pRow) => { if (pRow) { tmpProvider.cacheRecord(pRow); } }));
			});
		});
		return Promise.all(tmpReads);
	}

	// ── entry + load ─────────────────────────────────────────────────────────
	showDetail(pKey, pID)
	{
		if (!libDescriptors.Entities[pKey]) return;
		this._key = pKey;
		this._id = Number(pID);
		this._targetType = null;
		this._pending = {};
		// Fresh embedded-board state for this detail open. The token guards against a board fetch from a
		// previous navigation landing after we have moved on; _VisionBoardLoadedFor lets a re-render
		// (e.g. after editing a relation) re-mount the cached board without re-fetching.
		this._BoardNavToken = (this._BoardNavToken || 0) + 1;
		this._VisionBoardLoadedFor = null;
		this._VisionBoardID = null;
		this._VisionBoard = null;
		this._VisionBoardEditing = false;
		this._CommentsLoadedFor = null;
		this._StepsLoadedFor = null;
		this._fetch();
	}

	_fetch()
	{
		// The open record, resolved by id (not from the capped list). Reset per fetch. See WI #329.
		this._openRecord = null;
		let tmpDesc = libDescriptors.Entities[this._key];
		let tmpRelations = tmpDesc.Relations || [];
		// Load the entity caches this page resolves labels/pickers from: the four
		// SDLC bases, the open entity itself, and any relation Target/Source.
		let tmpLoadKeys = ['Vision', 'Module', 'Feature', 'WorkItem', this._key];
		tmpRelations.forEach((pRel) =>
		{
			if (pRel.Target && libDescriptors.Entities[pRel.Target]) { tmpLoadKeys.push(pRel.Target); }
			if (pRel.Source && libDescriptors.Entities[pRel.Source]) { tmpLoadKeys.push(pRel.Source); }
		});
		tmpLoadKeys = tmpLoadKeys.filter((pK, pIdx) => tmpLoadKeys.indexOf(pK) === pIdx);
		// resolve(), not list(): this view only needs to RESOLVE these records (labels, picker options),
		// not become the question a list screen is showing. list() would replace that screen's List,
		// reset its Page to 1 and overwrite its QueryKey out from under it -- opening any record used to
		// silently bounce the list behind it back to page one (WI #447). The rows still resolve() gives
		// back are kept here, for _buildSection's pickers to read instead of the shared store.
		this._entityRows = {};
		let tmpLoads = tmpLoadKeys.map((pK) => this._provider(pK).resolve().then((pRows) => { this._entityRows[pK] = pRows; }));
		// Per-vision words: a Vision's pages speak its own Drafting Kit. Resolve its
		// kit terms and label this page from them (falls back to the shell kit).
		this._scopeTerms = null;
		this._connectionStyle = null;
		this._stickerSet = null;
		this._stickerSetItems = null;
		if (this._key === 'Vision')
		{
			tmpLoads.push(libFrameworkClient.getJSON(this.pict, '/1.0/Vision/' + this._id + '/Lexicon')
				.catch(() => null)
				.then((pBody) => { if (pBody && pBody.Terms) { this._scopeTerms = pBody.Terms; } if (pBody && pBody.ConnectionStyle) { this._connectionStyle = pBody.ConnectionStyle; } if (pBody && typeof pBody.StickerSet === 'string') { this._stickerSet = pBody.StickerSet; } if (pBody && Array.isArray(pBody.StickerSetItems)) { this._stickerSetItems = pBody.StickerSetItems; } })
				.catch(() => {}));
		}
		// A vision picks its craft from the kits this tenant has imported (owned types only,
		// mirroring the import-only model). Load them to populate the header craft picker.
		this._ownedCrafts = [];
		if (libDescriptors.Entities[this._key].Craft && this.pict.providers.WorkflowAPI && typeof this.pict.providers.WorkflowAPI.getOwnedTypes === 'function')
		{
			tmpLoads.push(this.pict.providers.WorkflowAPI.getOwnedTypes()
				.then((pRows) => { this._ownedCrafts = Array.isArray(pRows) ? pRows : []; })
				.catch(() => {}));
		}
		this._blueprint = null;
		this._share = null;
		// Clear any pending blueprint pin selection carried over from a previously open entity (this
		// view is a reused singleton), so a fresh detail never opens with a stale item pre-selected.
		this._bpSelectedItem = 0;
		// Reset with the pin selection: this view is a reused singleton, so a stale subject would aim the
		// next entity's pins at the last one's blueprint.
		this._subject = null;
		if (libDescriptors.Entities[this._key].Blueprint)
		{
			// Addressing lives in BlueprintAPI now, so this box no longer knows which kind of entity it is
			// looking at, nor that a work item resolves to a different route than everything else.
			this._subject = this._bpAPI().subjectForEntity(this._key, this._id);
			tmpLoads.push(this._bpAPI().readSubject(this._subject)
				.then((pBody) => { this._blueprint = pBody || null; })
				.catch(() => {}));
			// Editors also load the public-share state so the compact block can show the share strip.
			if (this._canShare())
			{
				tmpLoads.push(libShareUI.fetchState(this.pict, this._subject).then((pSt) => { this._share = pSt || null; }).catch(() => {}));
			}
		}
		// Whiteboards attached to this entity. An owner can have several; the board section lists them
		// with a picker. Reset the selection on a fresh entity open (selectBoard/newBoard re-point it).
		this._ownerBoards = [];
		this._SelectedBoardID = 0;
		this._VisionBoardLoadedFor = null;

		if (libDescriptors.Entities[this._key].Moodboard && this.pict.providers.MoodboardAPI)
		{
			tmpLoads.push(this.pict.providers.MoodboardAPI.listForEntity(this._key, this._id)
				.then((pRows) =>
				{
					this._ownerBoards = Array.isArray(pRows) ? pRows : [];
					// A board named in the URL wins over "the first one", so a shared link opens what it says.
					let tmpWanted = Number(libFocus.readHashParams().board) || 0;
					let tmpNamed = tmpWanted && this._ownerBoards.find((pB) => Number(pB.IDMoodboard) === tmpWanted);
					if (tmpNamed) { this._SelectedBoardID = tmpNamed.IDMoodboard; }
					else if (this._ownerBoards[0]) { this._SelectedBoardID = this._ownerBoards[0].IDMoodboard; }
				})
				.catch(() => {}));
		}
		if (tmpRelations.some((pR) => pR.Render === 'sessions'))
		{
			tmpLoads.push(this.pict.providers.SessionAPI.listSessions());
		}
		// The Story timeline: the chronological set of sessions that advanced this
		// entity (descriptor Story: true -- WorkItem). Read from the dedicated endpoint
		// so it unions the scalar link and the SessionFileWorkItem join server-side.
		this._story = [];
		if (libDescriptors.Entities[this._key].Story && this.pict.providers.SessionAPI)
		{
			tmpLoads.push(this.pict.providers.SessionAPI.getWorkItemStory(this._id)
				.then((pRows) => { this._story = Array.isArray(pRows) ? pRows : []; })
				.catch(() => {}));
		}
		// The review history: the workflow WorkEvent timeline, surfaced for its qc.failed
		// (did-not-pass-QC) entries. Empty for a work item that never went through the gate.
		this._reviewHistory = [];
		if (libDescriptors.Entities[this._key].ReviewHistory && this.pict.providers.WorkflowAPI)
		{
			tmpLoads.push(this.pict.providers.WorkflowAPI.getTimeline(this._id)
				.then((pRows) => { this._reviewHistory = Array.isArray(pRows) ? pRows : []; })
				.catch(() => {}));
		}

		this._entityDocLinks = [];
		this._docTree = [];
		this._coreDocBody = '';
		this._coreDocPath = '';
		if (tmpRelations.some((pR) => pR.Render === 'docs' || pR.Render === 'core-doc' || pR.Render === 'doclink'))
		{
			tmpLoads.push(this.pict.providers.DocLakeAPI.tree().then((pNodes) => { this._docTree = pNodes; }));
			tmpLoads.push(this.pict.providers.LinkAPI.listLinksWhere('DocEntityLinks',
				[{ Field: 'EntityType', Value: this._key }, { Field: 'IDEntity', Value: this._id }]).then((pRows) => { this._entityDocLinks = pRows; }));
		}
		// Returned (WI #447's tests await it directly), which is otherwise unused: showDetail() fires
		// this and moves on, as it always has.
		return Promise.all(tmpLoads)
			.then(() => this._provider(this._key).read(this._id))
			.then((pOpenRecord) =>
			{
				// Resolve the open record directly by id and cache it, so the page renders even when the record
				// is outside the capped entity list (a large tenant; the list is /0/250 -- WI #329). Null means
				// it does not exist in this tenant; _rebuild falls back to the list cache, then a not-found state.
				this._openRecord = pOpenRecord || null;
				if (pOpenRecord) { this._provider(this._key).cacheRecord(pOpenRecord); }
				this._linkRows = {};
				this._mediaRows = {};
				this._subRows = {};
				let tmpJoinReads = tmpRelations.map((pRel, pIdx) =>
				{
					if (pRel.Render === 'link' || pRel.Render === 'inverse-join')
					{
						let tmpJoin = libDescriptors.Joins[pRel.Join];
						return this.pict.providers.LinkAPI.listLinks(tmpJoin.Plural, pRel.SelfField, this._id)
							.then((pRows) => { this._linkRows[pIdx] = pRows; });
					}
					if (pRel.Render === 'media')
					{
						return this.pict.providers.MediaAPI.listForOwner(this._key, this._id)
							.then((pRows) => { this._mediaRows[pIdx] = pRows; });
					}
					if (pRel.Render === 'subvisions')
					{
						let tmpJoin = libDescriptors.Joins[pRel.Join];
						return Promise.all(
							[
								this.pict.providers.LinkAPI.listLinks(tmpJoin.Plural, 'IDVisionSource', this._id),
								this.pict.providers.LinkAPI.listLinks(tmpJoin.Plural, 'IDVisionTarget', this._id)
							]).then((pRes) => { this._subRows[pIdx] = { Out: pRes[0] || [], In: pRes[1] || [] }; });
					}
					return Promise.resolve();
				});
				return this._ensureCoreDoc(tmpRelations)
					.then(() => Promise.all(tmpJoinReads))
					// Only for entities that actually draw the stacked relation sections (WI #448):
					// a Connections-grid entity's link/inverse-join chips render through the Connections
					// grid instead, which resolves its own titles server-side and never hits _label().
					.then(() => (tmpDesc.Connections ? null : this._resolveChipLabels(tmpRelations)))
					.then(() => this._loadCoreDocBody(tmpRelations));
			})
			.then(() => this._rebuild())
			// A single failed load in this fan-out (a transient server hiccup on any of the
			// entity caches, join reads, media, or docs) used to reject this whole chain with no
			// handler, so _rebuild never ran and the page was left blank -- the reported "forwarded
			// to a blank screen". Recover: tell the user, then rebuild from whatever is cached so the
			// header and fields still render and the page is usable (re-navigating retries the load).
			.catch((pErr) =>
			{
				if (this.pict && this.pict.log) { this.pict.log.warn('EntityDetail: a detail load failed (' + ((pErr && pErr.message) || pErr) + '); rendering from cache.'); }
				this._toast('Some of this page could not load: ' + ((pErr && pErr.message) || pErr), 'error');
				try { this._rebuild(); }
				catch (pRebuildErr) { if (this.pict && this.pict.log) { this.pict.log.error('EntityDetail: rebuild after load failure also threw: ' + ((pRebuildErr && pRebuildErr.message) || pRebuildErr)); } }
			});
	}

	_slug(pText) { return String(pText || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'doc'; }

	// The lake directory that holds an entity's documents. Vision docs match the
	// seed convention (entities/vision/<number>-<slug>); other entities use
	// entities/<type>/<id>.
	_entityDir(pRecord)
	{
		if (this._key === 'Vision') { return 'entities/vision/' + (pRecord.VisionNumber || this._id) + '-' + this._slug(pRecord.Name); }
		return 'entities/' + this._key.toLowerCase() + '/' + this._id;
	}

	// Ensure a Vision has one Core Vision Document in the lake, linked by
	// DocEntityLink(Role 'core'); create an empty one lazily on first open.
	_ensureCoreDoc(pRelations)
	{
		if (!pRelations.some((pR) => pR.Render === 'core-doc')) { return Promise.resolve(); }
		if ((this._entityDocLinks || []).some((pL) => String(pL.Role) === 'core')) { return Promise.resolve(); }
		let tmpProvider = this._provider(this._key);
		let tmpRecord = (tmpProvider && tmpProvider.store().Index[this._id]) || {};
		let tmpPath = this._entityDir(tmpRecord) + '/core.md';
		let tmpName = tmpRecord[libDescriptors.Entities[this._key].TitleField] || ('Vision #' + this._id);
		return this.pict.providers.DocLakeAPI.saveDoc(tmpPath, '# ' + tmpName + '\n')
			.then(() => this.pict.providers.LinkAPI.addLink('DocEntityLink',
				{ EntityType: this._key, IDEntity: this._id, DocPath: tmpPath, Role: 'core' }))
			.then((pRow) =>
			{
				this._entityDocLinks = (this._entityDocLinks || []).concat(
					[{ IDDocEntityLink: (pRow && pRow.IDDocEntityLink) || 0, EntityType: this._key, IDEntity: this._id, DocPath: tmpPath, Role: 'core' }]);
			})
			.catch(() => {});
	}

	// Read the core doc body from the lake so the detail pane can render it inline.
	_loadCoreDocBody(pRelations)
	{
		if (!pRelations.some((pR) => pR.Render === 'core-doc')) { return Promise.resolve(); }
		let tmpCore = (this._entityDocLinks || []).find((pL) => String(pL.Role) === 'core');
		if (!tmpCore) { this._coreDocBody = ''; this._coreDocPath = ''; return Promise.resolve(); }
		this._coreDocPath = tmpCore.DocPath;
		return this.pict.providers.DocLakeAPI.readDoc(tmpCore.DocPath).then((pBody) => { this._coreDocBody = pBody || ''; });
	}

	// Render the not-found state: a clear message in place of the field table, with every record section and
	// action zeroed out so nothing from a previous open leaks (this view is a reused singleton). An empty
	// .ps-detail-actions collapses via CSS, so hiding Edit is enough to hide the whole action bar. See WI #329.
	_renderNotFound(pState, pSingular)
	{
		let tmpSingular = pSingular || 'Record';
		pState.HeaderTitle = tmpSingular + ' not found';
		pState.Subtitle = '';
		pState.Fields = [];
		pState.Sections = [];
		pState.HeaderActions = [];
		pState.AddressToggle = [];
		pState.AddressedBadge = [];
		pState.DeleteSlot = [];
		pState.EditSlot = [];
		pState.CraftSlot = [];
		pState.BlueprintSlot = [];
		pState.BoardSlot = [];
		pState.LoopsSlot = [];
		pState.CoreDocSlot = [];
		pState.StepsSlot = [];
		pState.ConnectionsSlot = [];
		pState.WorkItemPromptsSlot = [];
		pState.StorySlot = [];
		pState.ReviewHistorySlot = [];
		pState.PromptsSlot = [];
		pState.ChecklistsSlot = [];
		pState.CommentsSlot = [];
		pState.NotFoundSlot = [{ Message: 'This ' + tmpSingular.toLowerCase() + ' could not be found. It may have been deleted, or it belongs to a different plan sheet.' }];
		let tmpRouter = this.pict.providers['Pict-Router'];
		if (tmpRouter && typeof tmpRouter.setDocumentTitle === 'function') { tmpRouter.setDocumentTitle(pState.HeaderTitle); }
		this.render();
	}

	// ── build the AppData section model from loaded data (no fetch) ───────────
	_rebuild()
	{
		let tmpDesc = libDescriptors.Entities[this._key];
		let tmpProvider = this._provider(this._key);
		// Prefer the record read directly by id (WI #329); fall back to the list cache so a record in the
		// first 250 still renders if the read-one endpoint is briefly unavailable.
		let tmpRecord = this._openRecord || (tmpProvider && tmpProvider.store().Index[this._id]) || {};
		// Stash the loaded record so mounts that run after the render (prompts panel) can read its
		// fields (e.g. WorkItemNumber for the copy-paste run block) without re-fetching.
		this._loadedRecord = tmpRecord;
		let tmpState = this.pict.AppData.EntityDetail;

		tmpState.Key = this._key;
		tmpState.BackLabel = libLabels.title(this.pict, this._key, this._scopeTerms, tmpDesc.Title);

		// The record does not exist in this tenant (deleted, a different plan sheet, or a stale/typo id):
		// render a clear not-found state rather than an empty shell titled with the raw database id (WI #329).
		if (!tmpRecord[tmpDesc.IDField])
		{
			this._renderNotFound(tmpState, libLabels.singular(this.pict, this._key, this._scopeTerms, tmpDesc.Title.replace(/s$/, '')));
			return;
		}
		tmpState.NotFoundSlot = [];
		tmpState.EditSlot = [{}];
		let tmpTitleText = tmpRecord[tmpDesc.TitleField] || ('#' + this._id);
		let tmpSingular = libLabels.singular(this.pict, this._key, this._scopeTerms, tmpDesc.Title.replace(/s$/, ''));
		// Entities with a per-customer human number (descriptor NumberField) lead with it.
		let tmpNumber = tmpDesc.NumberField ? (Number(tmpRecord[tmpDesc.NumberField]) || 0) : 0;
		tmpState.HeaderTitle = tmpNumber
			? (tmpSingular + ' #' + tmpNumber + ': ' + tmpTitleText)
			: (tmpSingular + ': ' + tmpTitleText);
		// Keep the document title on this record's own name so a bookmark of the page, and the
		// recent-pages history, read distinctly instead of a constant "Plansheet". The detail route has
		// no static title, so it reset to the default first; this refines it once the record is loaded.
		let tmpRouter = this.pict.providers['Pict-Router'];
		if (tmpRouter && typeof tmpRouter.setDocumentTitle === 'function') { tmpRouter.setDocumentTitle(String(tmpTitleText || tmpState.HeaderTitle || '')); }
		// The notebook history row for this page was recorded on navigation with the generic default
		// title ("Plansheet"), because the record had not loaded yet. Now that its real name is on the
		// document title, re-record so history reads distinctly; the provider relabels that same row.
		if (this.pict.PictApplication && typeof this.pict.PictApplication.recordCurrentView === 'function') { this.pict.PictApplication.recordCurrentView(); }
		tmpState.Subtitle = (this._key === 'Vision') ? this._fmt(tmpRecord.Summary) : '';
		// The title lives in the header now, so it is not repeated as a field row.
		tmpState.Fields = tmpDesc.Fields.filter((pF) => pF.F !== tmpDesc.TitleField).map((pF) =>
		{
			let tmpRaw = tmpRecord[pF.F];
			// Likert fields display as a dot scale; Markdown-flagged fields render
			// in onAfterRender into a per-field container (data stays out of HTML).
			if (pF.T === 'likert') { return { L: pF.L, Value: libDescriptors.formatLikert(tmpRaw), WideClass: '', MDSlot: [] }; }
			let tmpMD = !!(pF.Markdown && String(tmpRaw || '').trim());
			// Long fields break onto their own full-width row: markdown, a textarea, or a plain text value
			// long enough that it would otherwise fill a narrow stat cell and strand the next one (a Summary).
			let tmpLongText = (pF.T === 'text' && String(tmpRaw || '').length > 80);
			return (
			{
				L: pF.L,
				Value: tmpMD ? '' : this._fmt(tmpRaw),
				WideClass: (tmpMD || pF.T === 'textarea' || tmpLongText) ? 'is-wide' : '',
				MDSlot: tmpMD ? [{ ContainerID: 'Detail-MD-' + pF.F, Body: String(tmpRaw) }] : []
			});
		});
		// Quick-scan stat fields lead; long / markdown fields drop to their own full-width rows below.
		tmpState.Fields.sort((pA, pB) => (pA.WideClass ? 1 : 0) - (pB.WideClass ? 1 : 0));

		// Descriptor-declared header actions (e.g. Routine's "Start test run"),
		// shown to users who can edit content.
		let tmpApp = this.pict.PictApplication;
		let tmpCanAct = !!(tmpApp && typeof tmpApp._can === 'function' && tmpApp._can('content.edit'));
		tmpState.HeaderActions = (tmpCanAct ? (tmpDesc.HeaderActions || []) : []).map((pA) => ({ Label: pA.Label, Method: pA.Method }));

		// Addressable entities (Feedback): a header toggle to mark handled / reopen (edit
		// permission), and an "Addressed" badge with the date once it is marked.
		let tmpAddr = tmpDesc.Addressable;
		let tmpAddressedOn = !!(tmpAddr && libFocus.truthy(tmpRecord[tmpAddr.Field]));
		tmpState.AddressToggle = (tmpAddr && tmpCanAct)
			? [{ Label: tmpAddressedOn ? (tmpAddr.ReopenLabel || 'Reopen') : (tmpAddr.AddressLabel || 'Mark addressed') }]
			: [];
		let tmpAddressedDate = (tmpAddressedOn && tmpAddr.DateField && tmpRecord[tmpAddr.DateField]) ? String(tmpRecord[tmpAddr.DateField]).slice(0, 10) : '';
		tmpState.AddressedBadge = tmpAddressedOn
			? [{ Label: tmpAddressedDate ? ('Addressed ' + tmpAddressedDate) : 'Addressed' }]
			: [];
		// Header delete button (descriptor Deletable: true -- WorkItem). Same edit
		// permission as the other header actions; the record itself decides whether
		// deleting from here makes sense (soft delete, so it is always recoverable).
		tmpState.DeleteSlot = (tmpDesc.Deletable && tmpCanAct) ? [{}] : [];

		tmpState.BoardSlot = this._boardSlotFor(tmpDesc, tmpRecord);
		tmpState.CommentsSlot = this._commentsSlotFor(tmpDesc, tmpRecord);
		tmpState.ChecklistsSlot = this._checklistsSlotFor(tmpDesc, tmpRecord);
		tmpState.StepsSlot = this._stepsSlotFor(tmpDesc, tmpRecord);
		// A Connections-grid entity hosts its prompts inside the connections box, not as a separate section.
		tmpState.PromptsSlot = tmpDesc.Connections ? [] : this._promptsSlotFor(tmpDesc, tmpRecord);
		tmpState.BlueprintSlot = this._blueprintSlotFor(tmpDesc, tmpRecord);
		tmpState.StorySlot = this._storySlotFor(tmpDesc, tmpRecord);
		tmpState.ReviewHistorySlot = this._reviewHistorySlotFor(tmpDesc, tmpRecord);
		tmpState.CraftSlot = this._craftSlotFor(tmpDesc, tmpRecord);
		tmpState.LoopsSlot = this._loopsSlotFor(tmpDesc, tmpRecord);
		tmpState.ConnectionsSlot = this._connectionsSlotFor(tmpDesc, tmpRecord);
		tmpState.WorkItemPromptsSlot = this._workItemPromptsSlotFor(tmpDesc, tmpRecord);
		// The core document (Vision) is primary content, not a link list, so it gets its own
		// always-on slot -- unlike docs/media, the Connections grid has no equivalent for it, and a
		// Connections-grid entity's stacked relation Sections (below) are zeroed out.
		tmpState.CoreDocSlot = this._coreDocSlotFor(tmpDesc);
		// A Connections-grid entity shows its links in the grid, not the stacked relation sections.
		tmpState.Sections = tmpDesc.Connections ? [] : (tmpDesc.Relations || []).map((pRel, pIdx) => this._buildSection(pRel, pIdx, tmpRecord));
		this.render();
	}

	// Slot describing the embedded whiteboards section for entities that opt in (descriptor
	// Moodboard: true -- Vision and WorkItem). An owner can have several boards: a pill picker switches
	// the mounted one and a "new" button adds one. The label is the craft's Whiteboard word. Editing
	// needs content.edit. The board is mounted in onAfterRender (the container must exist first).
	_boardSlotFor(pDesc, pRecord)
	{
		if (!pDesc || !pDesc.Moodboard) { return []; }
		let tmpID = pRecord && pRecord[pDesc.IDField];
		if (!tmpID) { return []; }
		let tmpApp = this.pict.PictApplication;
		let tmpEditable = !!(tmpApp && typeof tmpApp._can === 'function' && tmpApp._can('content.edit'));
		let tmpBoards = this._ownerBoards || [];
		// A read-only viewer sees nothing useful in an all-empty board section: hide it entirely rather than
		// a header over a blank box. Editors keep it (they can start a board), and content on any board keeps
		// the section so a viewer can open the one that has content.
		if (!tmpEditable && this._allBoardsEmpty(tmpBoards)) { return []; }
		let tmpSelected = Number(this._SelectedBoardID) || (tmpBoards[0] && tmpBoards[0].IDMoodboard) || 0;
		let tmpPills = tmpBoards.map((pB) => ({ ID: pB.IDMoodboard, Name: pB.Name || ('Whiteboard #' + pB.IDMoodboard), ActiveClass: (Number(pB.IDMoodboard) === tmpSelected) ? 'is-active' : '' }));
		let tmpLabel = libLabels.plural(this.pict, 'Whiteboard', this._scopeTerms, 'Whiteboards');
		return [{
			ContainerID: 'Vision-Board',
			Label: tmpLabel,
			CanEdit: tmpEditable,
			Boards: tmpPills,
			AddSlot: tmpEditable ? [{}] : [],
			EditSlot: (tmpEditable && tmpBoards.length) ? [{}] : [],
			// Rename / disconnect / delete the selected board. Editors only, and only when a board exists.
			MenuSlot: (tmpEditable && tmpBoards.length) ? [{}] : [],
			EmptySlot: tmpBoards.length ? [] : [{ CanEdit: tmpEditable }]
		}];
	}

	
	// One-or-zero element slot describing an embedded comments section for entities that opt in
	// (descriptor Comments: true -- Vision and WorkItem). Posting needs content.edit; everyone else
	// gets a read-only thread. The section is mounted in onAfterRender (the container must exist first).
	_commentsSlotFor(pDesc, pRecord)
	{
		if (!pDesc || !pDesc.Comments) { return []; }
		let tmpID = pRecord && pRecord[pDesc.IDField];
		if (!tmpID) { return []; }
		let tmpApp = this.pict.PictApplication;
		let tmpEditable = !!(tmpApp && typeof tmpApp._can === 'function' && tmpApp._can('content.edit'));
		return [{ ContainerID: 'Entity-Comments', Label: 'Comments', Editable: tmpEditable }];
	}

	// One-or-zero element slot for the embedded checklists section (descriptor Checklist: true --
	// WorkItem). These are the SHARED, blueprint-visible checklists on the work item (private personal
	// lists live on the Checklists page, not here). Mounted in onAfterRender (the container must exist).
	_checklistsSlotFor(pDesc, pRecord)
	{
		if (!pDesc || !pDesc.Checklist) { return []; }
		let tmpID = pRecord && pRecord[pDesc.IDField];
		if (!tmpID) { return []; }
		return [{ ContainerID: 'Entity-Checklists', Label: 'Checklists' }];
	}

	// One-or-zero element slot for the embedded three-loops view (descriptor Loops: true -- Vision).
	// Mounted in onAfterRender (the container must exist first).
	_loopsSlotFor(pDesc, pRecord)
	{
		if (!pDesc || !pDesc.Loops) { return []; }
		let tmpID = pRecord && pRecord[pDesc.IDField];
		if (!tmpID) { return []; }
		return [{ ContainerID: 'Entity-VisionLoops', Label: 'Refinement loops' }];
	}

	// One-or-zero element slot for the inline Core Vision Document reader (descriptor relation
	// Render: 'core-doc' -- Vision). Always on regardless of Connections (the Vision detail page's
	// stacked relation Sections are zeroed out for Connections-grid entities, and the Connections
	// grid itself has no equivalent for a singleton document body).
	_coreDocSlotFor(pDesc)
	{
		let tmpRelations = (pDesc && pDesc.Relations) || [];
		let tmpRel = tmpRelations.find((pR) => pR.Render === 'core-doc');
		if (!tmpRel) { return []; }
		let tmpHasBody = !!String(this._coreDocBody || '').trim();
		return [{ Label: tmpRel.Label || 'Core document', HasBody: tmpHasBody ? [{}] : [], EmptySlot: tmpHasBody ? [] : [{}] }];
	}

	// One-or-zero element slot describing the embedded ordered-step editor for entities
	// that opt in (descriptor Steps: true -- Routine). Editing needs content.edit; everyone
	// else gets read-only step cards. Mounted in onAfterRender (the container must exist first).
	_stepsSlotFor(pDesc, pRecord)
	{
		if (!pDesc || !pDesc.Steps) { return []; }
		let tmpID = pRecord && pRecord[pDesc.IDField];
		if (!tmpID) { return []; }
		let tmpApp = this.pict.PictApplication;
		let tmpEditable = !!(tmpApp && typeof tmpApp._can === 'function' && tmpApp._can('content.edit'));
		return [{ ContainerID: 'Routine-Steps', Label: 'Steps', Editable: tmpEditable }];
	}

	// One-or-zero element slot describing the attached-prompts panel for entities that
	// opt in (descriptor Prompts: true -- WorkItem; TestRun gets it in the runner).
	// Attaching/detaching needs content.edit; everyone can read the pinned versions
	// and record runs. Mounted in onAfterRender (the container must exist first).
	_promptsSlotFor(pDesc, pRecord)
	{
		if (!pDesc || !pDesc.Prompts) { return []; }
		let tmpID = pRecord && pRecord[pDesc.IDField];
		if (!tmpID) { return []; }
		return [{ ContainerID: 'Entity-Prompts', Label: 'Prompts' }];
	}

	// One-or-zero element slot for the Blueprint title block (descriptor Blueprint:
	// true -- WorkItem). The visual is drawn into the container in onAfterRender
	// from the data loaded in _fetch.
	_blueprintSlotFor(pDesc, pRecord)
	{
		if (!pDesc || !pDesc.Blueprint) { return []; }
		let tmpID = pRecord && pRecord[pDesc.IDField];
		if (!tmpID) { return []; }
		return [{ ContainerID: 'Entity-Blueprint' }];
	}

	// Slot describing the Story panel (descriptor Story: true -- WorkItem): a chronological
	// timeline of the Claude sessions that advanced this work item. Read-only display; a
	// session is linked to a work item by the upload/MCP flow, and this panel narrates the
	// whole set (several sessions read as one story) with a link into each session's page.
	_storySlotFor(pDesc, pRecord)
	{
		if (!pDesc || !pDesc.Story) { return []; }
		let tmpID = pRecord && pRecord[pDesc.IDField];
		if (!tmpID) { return []; }
		let tmpKindLabels = { feedback: 'Feedback', prompt: 'Prompt generated', run: 'Prompt run', session: 'Session', validated: 'Validated', asbuilt: 'As-Built' };
		let tmpEntries = this._story || [];
		let tmpItems = tmpEntries.map((pE, pIdx) =>
		{
			let tmpKind = pE.Kind || 'session';
			let tmpIsSession = (tmpKind === 'session');
			// Session entries keep their richness (model chip, stat chips, outcome badge); the other loop
			// moments (feedback / prompt / run / validated) show a kind badge + a one-line detail.
			let tmpStats = [];
			let tmpModelSlot = [];
			let tmpOutcomeLabel = '';
			let tmpOutcomeClass = '';
			if (tmpIsSession)
			{
				if (pE.PromptCount) { tmpStats.push({ Text: pE.PromptCount + (pE.PromptCount === 1 ? ' prompt' : ' prompts') }); }
				if (pE.ToolCallCount) { tmpStats.push({ Text: pE.ToolCallCount + (pE.ToolCallCount === 1 ? ' tool call' : ' tool calls') }); }
				if (pE.AgentCount) { tmpStats.push({ Text: pE.AgentCount + (pE.AgentCount === 1 ? ' agent' : ' agents') }); }
				let tmpDuration = this._storyDuration(pE.TotalDurationMs);
				if (tmpDuration) { tmpStats.push({ Text: tmpDuration }); }
				if (pE.Model) { tmpModelSlot = [{ Model: pE.Model }]; }
				tmpOutcomeLabel = pE.Outcome || 'Unknown';
				tmpOutcomeClass = 'ps-story-outcome-' + String(tmpOutcomeLabel).toLowerCase();
			}
			else if (tmpKind === 'run' && pE.Outcome)
			{
				tmpOutcomeLabel = pE.Outcome;
				tmpOutcomeClass = 'ps-story-outcome-' + String(pE.Outcome).toLowerCase();
			}
			return (
			{
				ID: pE.ID || 0,
				Num: pIdx + 1,
				KindLabel: tmpKindLabels[tmpKind] || tmpKind,
				KindClass: 'ps-story-kind-' + tmpKind,
				LinkSlot: tmpIsSession ? [{ ID: pE.ID, Title: pE.Title || ('Session #' + pE.ID) }] : [],
				PlainSlot: tmpIsSession ? [] : [{ Title: pE.Title || '' }],
				When: this._storyWhen(pE.At),
				ModelSlot: tmpModelSlot,
				DetailSlot: (!tmpIsSession && pE.Detail) ? [{ Detail: pE.Detail }] : [],
				OutcomeLabel: tmpOutcomeLabel,
				OutcomeClass: tmpOutcomeClass,
				Stats: tmpStats
			});
		});
		return [{
			Label: 'Story',
			CountLabel: tmpItems.length ? (tmpItems.length + (tmpItems.length === 1 ? ' event' : ' events')) : '',
			Items: tmpItems,
			EmptySlot: tmpItems.length ? [] : [{}]
		}];
	}

	// Slot describing the Review history panel (descriptor ReviewHistory: true -- WorkItem): the
	// QC decisions from the workflow WorkEvent timeline. Surfaces each qc.failed (did-not-pass-QC)
	// event with its reason, note, and linked-issue count, plus a summary line. The panel only
	// appears once a work item has some review history (a fail, rework, or a passed validation).
	_reviewHistorySlotFor(pDesc, pRecord)
	{
		if (!pDesc || !pDesc.ReviewHistory) { return []; }
		let tmpID = pRecord && pRecord[pDesc.IDField];
		if (!tmpID) { return []; }
		let tmpFails = (this._reviewHistory || []).filter((pEvent) => pEvent && pEvent.Type === 'qc.failed');
		let tmpItems = tmpFails.map((pEvent, pIdx) =>
		{
			let tmpPayload = pEvent.Payload || {};
			let tmpIssueCount = Array.isArray(tmpPayload.IDIssues) ? tmpPayload.IDIssues.length : 0;
			return (
			{
				Num: pIdx + 1,
				Reason: tmpPayload.ReasonCode || 'Other',
				When: pEvent.At ? this._storyWhen(new Date(pEvent.At).toISOString()) : '',
				NoteSlot: tmpPayload.Note ? [{ Note: tmpPayload.Note }] : [],
				IssuesSlot: tmpIssueCount ? [{ Label: tmpIssueCount + (tmpIssueCount === 1 ? ' issue' : ' issues') }] : []
			});
		});
		let tmpRework = Number(pRecord.ReworkCount) || 0;
		let tmpValidated = !!pRecord.ValidationDate;
		if (!tmpItems.length && !tmpRework && !tmpValidated) { return []; }
		let tmpSummary = tmpValidated
			? ('Passed review' + (this._storyWhen(pRecord.ValidationDate) ? (' ' + this._storyWhen(pRecord.ValidationDate)) : ''))
			: (tmpRework ? ('Sent back ' + tmpRework + (tmpRework === 1 ? ' time' : ' times')) : 'In review');
		return [{
			Label: 'Review history',
			Summary: tmpSummary,
			SummaryClass: tmpValidated ? 'ps-review-ok' : (tmpRework ? 'ps-review-warn' : ''),
			Items: tmpItems,
			EmptySlot: tmpItems.length ? [] : [{}]
		}];
	}

	// Human duration for a session length in milliseconds (drops to '' for zero/unknown).
	_storyDuration(pMs)
	{
		let tmpMs = Number(pMs) || 0;
		if (tmpMs < 1000) { return ''; }
		let tmpMinutes = Math.round(tmpMs / 60000);
		if (tmpMinutes < 1) { return 'under a minute'; }
		if (tmpMinutes < 60) { return tmpMinutes + 'm'; }
		let tmpHours = Math.floor(tmpMinutes / 60);
		let tmpRem = tmpMinutes % 60;
		return tmpRem ? (tmpHours + 'h ' + tmpRem + 'm') : (tmpHours + 'h');
	}

	// Readable timestamp for a session start (falls back to the raw string if unparseable).
	_storyWhen(pTimestamp)
	{
		if (!pTimestamp) { return ''; }
		let tmpDate = new Date(pTimestamp);
		if (isNaN(tmpDate.getTime())) { return String(pTimestamp); }
		return tmpDate.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
	}

	// One-or-zero element slot for the connections grid (descriptor Connections: true). The grid takes
	// the place of the entity's stacked relation sections; mounted in onAfterRender (container first).
	_connectionsSlotFor(pDesc, pRecord)
	{
		if (!pDesc || !pDesc.Connections) { return []; }
		let tmpID = pRecord && pRecord[pDesc.IDField];
		if (!tmpID) { return []; }
		return [{ ContainerID: 'Entity-Connections' }];
	}

	// One-or-zero element slot for a read-only "prompts from linked work items" section (descriptor
	// WorkItemPrompts: true -- Feedback). It surfaces the prompts of the work items this record links
	// to (via the FeedbackWorkItem join), so the feedback page can read + copy them without leaving.
	// The prompts stay owned by their work item; this is a display through the relation. Mounted in
	// onAfterRender (the container must exist first).
	_workItemPromptsSlotFor(pDesc, pRecord)
	{
		if (!pDesc || !pDesc.WorkItemPrompts) { return []; }
		let tmpID = pRecord && pRecord[pDesc.IDField];
		if (!tmpID) { return []; }
		return [{ ContainerID: 'Entity-WorkItemPrompts', Label: 'Prompts from linked work items' }];
	}

	// One-or-zero element slot for the header craft picker (descriptor Craft: true -- Vision). Lists
	// the kits this tenant has imported plus a "House default" (clear). Editors get a <select> that
	// re-points the vision's craft; everyone else sees the current craft name. The vision's words come
	// from its craft once set, so changing it relabels this page (and its work items' blueprints).
	_craftSlotFor(pDesc, pRecord)
	{
		if (!pDesc || !pDesc.Craft) { return []; }
		let tmpID = pRecord && pRecord[pDesc.IDField];
		if (!tmpID) { return []; }
		let tmpApp = this.pict.PictApplication;
		let tmpEditable = !!(tmpApp && typeof tmpApp._can === 'function' && tmpApp._can('content.edit'));
		let tmpCurrent = Number(pRecord.IDVisionType) || 0;
		let tmpOwned = this._ownedCrafts || [];
		let tmpMatch = tmpOwned.find((pC) => Number(pC.ID) === tmpCurrent);
		let tmpCurrentName = tmpMatch ? (tmpMatch.Name || tmpMatch.TypeKey || ('Kit ' + tmpMatch.ID)) : 'House default';
		let tmpOptions = [{ Value: 0, Label: 'House default', SelectedAttr: tmpCurrent ? '' : 'selected' }]
			.concat(tmpOwned.map((pC) => ({ Value: pC.ID, Label: pC.Name || pC.TypeKey || ('Kit ' + pC.ID), SelectedAttr: (Number(pC.ID) === tmpCurrent ? 'selected' : '') })));
		return [{
			EditSlot: tmpEditable ? [{ Options: tmpOptions }] : [],
			ReadSlot: tmpEditable ? [] : [{ Name: tmpCurrentName }]
		}];
	}

	// Re-point this vision's craft, persist it, then reload so the page (and blueprints) relabel.
	setCraft(pValue)
	{
		let tmpIDVisionType = parseInt(pValue, 10) || 0;
		libFrameworkClient.putJSON(this.pict, '/1.0/Vision/' + this._id + '/Craft', { IDVisionType: tmpIDVisionType })
			.then(() => this._fetch())
			.catch((pError) => { if (this.pict && this.pict.log) { this.pict.log.warn('Could not set the vision craft: ' + pError.message); } });
	}

	_buildSection(pRel, pIdx, pRecord)
	{
		let tmpSection = { Idx: pIdx, Label: pRel.Label, FkSlot: [], LinkSlot: [], InverseSlot: [], TargetSlot: [], SessionsSlot: [], DocsSlot: [], MediaSlot: [], DocLinkSlot: [], SubVisionSlot: [] };

		if (pRel.Render === 'fk')
		{
			// A server-backed candidate search (WI #446), not this._rows(pRel.Target) -- that is at most
			// one page (WI #433), so a target past it could not be chosen. Mounted post-render by
			// _mountRelationPickers(); this slot just carries the host div. See CandidateSearch.js.
			//
			// LIVE for Contact and Call, the two 'fk' entities that are not Connections:true -- this
			// stacked-sections UI is genuinely how a person edits their fk fields today. Every OTHER 'fk'
			// entity (Feature, Feedback, AsBuilt, Issue, Expectation, Deliverable) is Connections:true,
			// which zeroes Sections entirely in _rebuild() (`tmpDesc.Connections ? [] : ...`) -- the
			// Connections grid replaces this UI for them, fed by a different, already-unbounded server
			// endpoint (Connections-Endpoint.js Candidates, no page cap) that does not have WI #446's bug.
			// Kept converted (consistent, correct if a descriptor ever drops Connections) rather than
			// special-cased away.
			tmpSection.FkSlot = [{ Field: pRel.Field, Idx: pIdx, ContainerID: 'rel-fk-host-' + pIdx }];
		}
		else if (pRel.Render === 'link')
		{
			let tmpJoin = libDescriptors.Joins[pRel.Join];
			let tmpRows = this._linkRows[pIdx] || [];
			let tmpItems = tmpRows.filter((pR) => String(pR.RelationType) !== 'Decomposes').map((pR) =>
				({
					Idx: pIdx, JoinID: pR[tmpJoin.ID],
					TargetKey: pRel.Target, TargetID: pR[pRel.TargetField],
					Label: this._label(pRel.Target, pR[pRel.TargetField]),
					RelType: pRel.RelationType ? (pR.RelationType || '') : ''
				}));
			// A server-backed candidate search (WI #446), not this._rows(pRel.Target) -- see the fk branch
			// above. AddSlot used to be gated on the current page holding an unlinked candidate; with a
			// server search there is no "current page" to check, so it is always offered now (a bug fix in
			// its own right -- a real candidate past page one used to hide the add UI entirely). Already-
			// linked ids and (a self-referencing relation) this record's own id are excluded server-side
			// results, not filtered out of a precomputed list; _mountRelationPickers() supplies them.
			//
			// EVERY entity that declares Render: 'link' (Vision, Feature, WorkItem, Issue, Feedback) is
			// also Connections: true, so -- same as the fk branch's note on Feature/Feedback/Issue/etc --
			// this AddSlot never actually paints; the Connections grid's own linker is the live UI, backed
			// by Connections-Endpoint.js Candidates (already unbounded, no page cap). Kept converted rather
			// than special-cased away.
			tmpSection.LinkSlot = [{
				Idx: pIdx,
				Items: tmpItems,
				EmptySlot: tmpItems.length ? [] : [{}],
				AddSlot: [{
					Idx: pIdx,
					ContainerID: 'rel-link-add-host-' + pIdx,
					RelTypeSlot: pRel.RelationType ? [{ Idx: pIdx, Options: libDescriptors.RelationTypes.map((pT) => ({ Value: pT })) }] : []
				}]
			}];
		}
		else if (pRel.Render === 'inverse-join')
		{
			let tmpRows = this._linkRows[pIdx] || [];
			let tmpItems = tmpRows.map((pR) => ({ TargetKey: pRel.Target, TargetID: pR[pRel.MapField], Label: this._label(pRel.Target, pR[pRel.MapField]), Sub: '' }));
			tmpSection.InverseSlot = [{ Items: tmpItems, EmptySlot: tmpItems.length ? [] : [{}] }];
		}
		else if (pRel.Render === 'inverse-fk')
		{
			let tmpSourceDesc = libDescriptors.Entities[pRel.Source];
			let tmpList = this._rows(pRel.Source).filter((pR) => String(pR[pRel.SourceField]) === String(this._id));
			let tmpItems = tmpList.map((pR) =>
				({ TargetKey: pRel.Source, TargetID: pR[tmpSourceDesc.IDField], Label: pR[tmpSourceDesc.TitleField] || ('#' + pR[tmpSourceDesc.IDField]), Sub: (pRel.SubField ? pR[pRel.SubField] : (pR.Status || pR.Type)) || '' }));
			tmpSection.InverseSlot = [{
				Items: tmpItems,
				EmptySlot: tmpItems.length ? [] : [{}],
				// Optional create-from-here button (Create: { Label, Prefill }).
				CreateSlot: pRel.Create ? [{ Idx: pIdx, Label: pRel.Create.Label }] : []
			}];
		}
		else if (pRel.Render === 'target')
		{
			// Converted for WI #446 (a server-backed candidate search, not this._rows(tmpDraftType) -- see
			// the fk branch above), but WorkItem is the only entity that declares Render: 'target' and
			// WorkItem also declares Connections: true, which zeroes out Sections entirely in _rebuild()
			// (`tmpDesc.Connections ? [] : ...`) -- the Connections grid takes the stacked sections' place
			// for those entities, and its own manifest (Connections-Endpoint.js) has no 'about' entry, so
			// this branch never actually paints in the shipped UI. Left converted (correct if it is ever
			// reconnected) rather than deleted, and flagged rather than silently left as dead code passing
			// for a fix -- the two live "This work is about" pickers (spawnWorkItemFromFeedback,
			// spawnWorkItemFromIssue, both create-time) are the ones WI #446 actually fixed.
			let tmpDraftType = this._targetType || pRecord.TargetType || 'None';
			let tmpTypeOptions = libDescriptors.WorkItemTargetTypes.map((pT) => ({ Value: pT, SelectedAttr: (pT === tmpDraftType ? 'selected' : '') }));
			let tmpEntitySlot = (tmpDraftType !== 'None' && libDescriptors.Entities[tmpDraftType])
				? [{ ContainerID: 'rel-target-host-' + pIdx }] : [];
			tmpSection.TargetSlot = [{ TypeOptions: tmpTypeOptions, EntitySlot: tmpEntitySlot }];
		}
		else if (pRel.Render === 'sessions')
		{
			let tmpAll = (this.pict.AppData.Sessions && this.pict.AppData.Sessions.List) || [];
			let tmpItems = tmpAll.filter((pS) => String(pS[pRel.Field]) === String(this._id)).map((pS) =>
				({
					ID: pS.IDSessionFile,
					Title: pS.Title || pS.OriginalFileName || ('Session #' + pS.IDSessionFile),
					Field: pRel.Field,
					OutcomeOptions: libDescriptors.WorkOutcomes.map((pO) => ({ Value: pO, SelectedAttr: (String(pO) === String(pS.WorkOutcome || 'Unknown') ? 'selected' : '') }))
				}));
			let tmpAddOptions = tmpAll.filter((pS) => String(pS[pRel.Field]) !== String(this._id)).map((pS) =>
				({ Value: pS.IDSessionFile, Label: pS.Title || ('Session #' + pS.IDSessionFile) }));
			tmpSection.SessionsSlot = [{
				Idx: pIdx,
				Items: tmpItems,
				EmptySlot: tmpItems.length ? [] : [{}],
				AddSlot: tmpAddOptions.length ? [{ Idx: pIdx, Options: [{ Value: '', Label: '— select a session —' }].concat(tmpAddOptions) }] : []
			}];
		}
		else if (pRel.Render === 'docs')
		{
			let tmpDocLake = this.pict.providers.DocLakeAPI;
			let tmpItems = (this._entityDocLinks || []).filter((pL) => String(pL.Role) === 'doc').map((pL) =>
				({ JoinID: pL.IDDocEntityLink, Path: pL.DocPath, Title: tmpDocLake.titleFor(pL.DocPath) || '(untitled)' }));
			tmpSection.DocsSlot = [{ Items: tmpItems, EmptySlot: tmpItems.length ? [] : [{}] }];
		}
		else if (pRel.Render === 'media')
		{
			let tmpList = this._mediaRows[pIdx] || [];
			let tmpItems = tmpList.map((pM) =>
			{
				let tmpIsImage = (String(pM.MimeType || '').indexOf('image/') === 0) || (pM.MediaType === 'Screenshot');
				// Videos get an inline <video controls> (the blob endpoint serves byte
				// ranges, so seeking works); the MimeType prefix is the test because
				// MediaType 'Recording' also covers audio.
				let tmpIsVideo = String(pM.MimeType || '').indexOf('video/') === 0;
				let tmpURL = '/1.0/Media/' + pM.IDMedia + '/Blob';
				let tmpTitle = pM.Title || pM.OriginalFileName || ('Media #' + pM.IDMedia);
				return (
				{
					ID: pM.IDMedia, Field: pRel.Field, Title: tmpTitle, URL: tmpURL,
					ImageSlot: tmpIsImage ? [{ URL: tmpURL, Title: tmpTitle }] : [],
					VideoSlot: tmpIsVideo ? [{ URL: tmpURL, Title: tmpTitle }] : [],
					FileSlot: (tmpIsImage || tmpIsVideo) ? [] : [{ URL: tmpURL, Title: tmpTitle, Type: pM.MediaType || 'file' }]
				});
			});
			tmpSection.MediaSlot = [{ Idx: pIdx, Items: tmpItems, EmptySlot: tmpItems.length ? [] : [{}] }];
		}
		else if (pRel.Render === 'doclink')
		{
			let tmpDocLake = this.pict.providers.DocLakeAPI;
			let tmpRefs = (this._entityDocLinks || []).filter((pL) => String(pL.Role) === 'ref');
			let tmpLinkedPaths = tmpRefs.map((pL) => pL.DocPath);
			let tmpItems = tmpRefs.map((pL) =>
				({ Idx: pIdx, JoinID: pL.IDDocEntityLink, Path: pL.DocPath, Title: tmpDocLake.titleFor(pL.DocPath) || '(untitled)' }));
			let tmpAddOptions = (this._docTree || [])
				.filter((pN) => pN.NodeType === 'file'
					&& String(pN.Path).indexOf('entities/') !== 0
					&& String(pN.Name).charAt(0) !== '_'
					&& tmpLinkedPaths.indexOf(pN.Path) < 0)
				.map((pN) => ({ Value: pN.Path, Label: pN.Title || pN.Name }));
			tmpSection.DocLinkSlot = [{
				Idx: pIdx,
				Items: tmpItems,
				EmptySlot: tmpItems.length ? [] : [{}],
				AddSlot: tmpAddOptions.length ? [{ Idx: pIdx, Options: [{ Value: '', Label: '— link a document —' }].concat(tmpAddOptions) }] : []
			}];
		}

		else if (pRel.Render === 'subvisions')
		{
			let tmpJoin = libDescriptors.Joins[pRel.Join];
			let tmpSub = this._subRows[pIdx] || { Out: [], In: [] };
			let tmpChildRows = (tmpSub.Out || []).filter((pR) => String(pR.RelationType) === 'Decomposes');
			let tmpParentRows = (tmpSub.In || []).filter((pR) => String(pR.RelationType) === 'Decomposes');
			let tmpVisions = (this._provider('Vision').store().Index) || {};
			let tmpChildren = tmpChildRows.map((pR) =>
			{
				let tmpV = tmpVisions[pR.IDVisionTarget] || {};
				return { Idx: pIdx, JoinID: pR[tmpJoin.ID], TargetID: pR.IDVisionTarget, Number: tmpV.VisionNumber || '?', Name: tmpV.Name || ('#' + pR.IDVisionTarget), Status: tmpV.Status || '' };
			});
			let tmpParents = tmpParentRows.map((pR) =>
			{
				let tmpV = tmpVisions[pR.IDVisionSource] || {};
				return { TargetID: pR.IDVisionSource, Number: tmpV.VisionNumber || '?', Name: tmpV.Name || ('#' + pR.IDVisionSource) };
			});
			// A server-backed candidate search (WI #446), not this._rows('Vision') -- see the fk branch
			// above. Excludes (this record's own id, and every vision already a part) are recomputed the
			// same way by _mountRelationPickers().
			//
			// Vision is Connections: true (same as the link branch's note), so this AddSlot never actually
			// paints either -- the live "Parts of this vision" add-UI is the Connections grid's own linker
			// (Connections-Endpoint.js declares it Linkable), already unbounded. Kept converted rather than
			// special-cased away.
			tmpSection.SubVisionSlot = [{
				Idx: pIdx,
				Children: tmpChildren,
				ChildEmpty: tmpChildren.length ? [] : [{}],
				ParentSlot: tmpParents.length ? [{ Items: tmpParents }] : [],
				AddSlot: [{ Idx: pIdx, ContainerID: 'rel-subv-host-' + pIdx }]
			}];
		}

		return tmpSection;
	}

	// ── mutations ────────────────────────────────────────────────────────────
	setFk(pField, pValue)
	{
		let tmpProvider = this._provider(this._key);
		let tmpRec = {};
		tmpRec[pField] = pValue ? Number(pValue) : 0;
		// Refreshed by showDetail() -> _fetch()'s own resolve() of this._key -- a prior explicit
		// list() here was redundant, and (WI #447) reset this entity's list screen to page one.
		tmpProvider.update(this._id, tmpRec)
			.then(() => this.showDetail(this._key, this._id))
			.catch((pErr) => this._toast('Update failed: ' + (pErr.message || pErr), 'error'));
	}

	setPendingTarget(pIdx, pValue) { this._pending[pIdx] = this._pending[pIdx] || {}; this._pending[pIdx].Target = pValue; }
	setPendingRelType(pIdx, pValue) { this._pending[pIdx] = this._pending[pIdx] || {}; this._pending[pIdx].RelType = pValue; }

	addLink(pIdx)
	{
		let tmpRel = libDescriptors.Entities[this._key].Relations[pIdx];
		let tmpJoin = libDescriptors.Joins[tmpRel.Join];
		let tmpPending = this._pending[pIdx] || {};
		if (!tmpPending.Target) { return this._toast('Pick something to link first.', 'info'); }
		let tmpRec = {};
		tmpRec[tmpRel.SelfField] = this._id;
		tmpRec[tmpRel.TargetField] = Number(tmpPending.Target);
		if (tmpRel.RelationType) { tmpRec.RelationType = tmpPending.RelType || libDescriptors.RelationTypes[0]; }
		this.pict.providers.LinkAPI.addLink(tmpJoin.Entity, tmpRec)
			.then(() => { this._pending[pIdx] = null; this.showDetail(this._key, this._id); })
			.catch((pErr) => this._toast('Link failed: ' + (pErr.message || pErr), 'error'));
	}

	removeLink(pIdx, pJoinID)
	{
		let tmpRel = libDescriptors.Entities[this._key].Relations[pIdx];
		let tmpJoin = libDescriptors.Joins[tmpRel.Join];
		this.pict.providers.LinkAPI.removeLink(tmpJoin.Entity, pJoinID)
			.then(() => this.showDetail(this._key, this._id))
			.catch((pErr) => this._toast('Unlink failed: ' + (pErr.message || pErr), 'error'));
	}

	setTargetType(pValue) { this._targetType = pValue; this._rebuild(); }

	setTarget(pValue)
	{
		let tmpProvider = this._provider('WorkItem');
		let tmpType = this._targetType || 'None';
		let tmpRec = { TargetType: (pValue && tmpType !== 'None') ? tmpType : 'None', IDTargetVision: 0, IDTargetModule: 0, IDTargetFeature: 0 };
		if (pValue && tmpType !== 'None') { tmpRec['IDTarget' + tmpType] = Number(pValue); }
		// See setFk above: showDetail() -> _fetch() already re-resolves this entity.
		tmpProvider.update(this._id, tmpRec)
			.then(() => this.showDetail(this._key, this._id))
			.catch((pErr) => this._toast('Update failed: ' + (pErr.message || pErr), 'error'));
	}

	// ── sessions (Phase 3) ───────────────────────────────────────────────────
	setPendingSession(pIdx, pValue) { this._pending['s' + pIdx] = pValue; }

	linkSession(pIdx)
	{
		let tmpRel = libDescriptors.Entities[this._key].Relations[pIdx];
		let tmpPending = this._pending['s' + pIdx];
		if (!tmpPending) { return this._toast('Pick a session first.', 'info'); }
		let tmpPatch = {};
		tmpPatch[tmpRel.Field] = this._id;
		this.pict.providers.SessionAPI.setLink(Number(tmpPending), tmpPatch)
			.then(() => { this._pending['s' + pIdx] = null; this.showDetail(this._key, this._id); })
			.catch((pErr) => this._toast('Link failed: ' + (pErr.message || pErr), 'error'));
	}

	unlinkSession(pField, pIDSession)
	{
		let tmpPatch = {};
		tmpPatch[pField] = 0;
		this.pict.providers.SessionAPI.setLink(pIDSession, tmpPatch)
			.then(() => this.showDetail(this._key, this._id))
			.catch((pErr) => this._toast('Unlink failed: ' + (pErr.message || pErr), 'error'));
	}

	setSessionOutcome(pIDSession, pValue)
	{
		this.pict.providers.SessionAPI.setLink(pIDSession, { WorkOutcome: pValue })
			.then(() => this.showDetail(this._key, this._id))
			.catch((pErr) => this._toast('Update failed: ' + (pErr.message || pErr), 'error'));
	}

	// ── documentation (Docs Lake; addressed by path) ─────────────────────────
	openDoc(pPath)
	{
		let tmpApp = this.pict.PictApplication;
		let tmpLabel = this.pict.providers.DocLakeAPI.titleFor(pPath) || 'Edit document';
		if (tmpApp && typeof tmpApp.showDocEditor === 'function') tmpApp.showDocEditor(this._key, this._id, pPath, tmpLabel);
	}

	newDoc()
	{
		let tmpProvider = this._provider(this._key);
		let tmpRecord = (tmpProvider && tmpProvider.store().Index[this._id]) || {};
		let tmpBase = this._entityDir(tmpRecord) + '/note-';
		let tmpExisting = (this._entityDocLinks || []).map((pL) => pL.DocPath);
		let tmpN = 1;
		while (tmpExisting.indexOf(tmpBase + tmpN + '.md') >= 0) { tmpN++; }
		let tmpPath = tmpBase + tmpN + '.md';
		this.pict.providers.DocLakeAPI.saveDoc(tmpPath, '# New document\n')
			.then(() => this.pict.providers.LinkAPI.addLink('DocEntityLink',
				{ EntityType: this._key, IDEntity: this._id, DocPath: tmpPath, Role: 'doc' }))
			.then(() =>
			{
				let tmpApp = this.pict.PictApplication;
				if (tmpApp && typeof tmpApp.showDocEditor === 'function') tmpApp.showDocEditor(this._key, this._id, tmpPath, 'Edit document');
			})
			.catch((pErr) => this._toast('Could not create document: ' + (pErr.message || pErr), 'error'));
	}

	removeDoc(pJoinID, pPath)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		let tmpDo = () => this.pict.providers.LinkAPI.removeLink('DocEntityLink', pJoinID)
			.then(() => this.pict.providers.DocLakeAPI.deleteDoc(pPath))
			.then(() => this.showDetail(this._key, this._id))
			.catch((pErr) => this._toast('Delete failed: ' + (pErr.message || pErr), 'error'));
		if (tmpModal && typeof tmpModal.confirm === 'function')
		{
			tmpModal.confirm('Delete this document?', { dangerous: true }).then((pOk) => { if (pOk) tmpDo(); });
		}
		else { tmpDo(); }
	}

	openCoreDoc()
	{
		let tmpApp = this.pict.PictApplication;
		if (!this._coreDocPath) { return this._toast('No core document yet.', 'info'); }
		if (tmpApp && typeof tmpApp.showDocEditor === 'function') tmpApp.showDocEditor(this._key, this._id, this._coreDocPath, 'Core Vision Document');
	}

	// ── media (Phase 5) ──────────────────────────────────────────────────────
	uploadMedia(pIdx, pFile)
	{
		if (!pFile) return;
		this._toast('Uploading ' + (pFile.name || 'file') + '…', 'info');
		this.pict.providers.MediaAPI.upload(this._key, this._id, pFile)
			.then(() => { this._toast('Uploaded.', 'success'); this.showDetail(this._key, this._id); })
			.catch((pErr) => this._toast('Upload failed: ' + (pErr.message || pErr), 'error'));
	}

	removeMedia(pID)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		let tmpDo = () => this.pict.providers.MediaAPI.remove(pID)
			.then(() => this.showDetail(this._key, this._id))
			.catch((pErr) => this._toast('Remove failed: ' + (pErr.message || pErr), 'error'));
		if (tmpModal && typeof tmpModal.confirm === 'function')
		{
			tmpModal.confirm('Remove this media?', { dangerous: true }).then((pOk) => { if (pOk) tmpDo(); });
		}
		else { tmpDo(); }
	}

	// ── curated doc references (tie foundational docs back to entities) ──────
	setPendingDocLink(pIdx, pValue) { this._pending['dl' + pIdx] = pValue; }

	addDocLink(pIdx)
	{
		let tmpPending = this._pending['dl' + pIdx];
		if (!tmpPending) { return this._toast('Pick a document to link.', 'info'); }
		this.pict.providers.LinkAPI.addLink('DocEntityLink',
			{ EntityType: this._key, IDEntity: this._id, DocPath: String(tmpPending), Role: 'ref' })
			.then(() => { this._pending['dl' + pIdx] = null; this.showDetail(this._key, this._id); })
			.catch((pErr) => this._toast('Link failed: ' + (pErr.message || pErr), 'error'));
	}

	removeDocLink(pIdx, pJoinID)
	{
		this.pict.providers.LinkAPI.removeLink('DocEntityLink', pJoinID)
			.then(() => this.showDetail(this._key, this._id))
			.catch((pErr) => this._toast('Unlink failed: ' + (pErr.message || pErr), 'error'));
	}

	openFoundationDoc(pPath)
	{
		let tmpApp = this.pict.PictApplication;
		if (tmpApp && typeof tmpApp.showFoundationDoc === 'function') tmpApp.showFoundationDoc(pPath);
	}

	// ── sub-visions (decomposition via VisionVisionJoin RelationType 'Decomposes') ──
	setPendingSubName(pIdx, pValue) { this._pending['svn' + pIdx] = pValue; }
	setPendingSubLink(pIdx, pValue) { this._pending['svl' + pIdx] = pValue; }

	createSubVision(pIdx)
	{
		let tmpName = String(this._pending['svn' + pIdx] || '').trim();
		if (!tmpName) { return this._toast('Name the sub-vision.', 'info'); }
		let tmpParentID = this._id;
		this.pict.providers.VisionAPI.create({ Name: tmpName, Status: 'Draft' })
			.then((pVision) =>
			{
				let tmpChildID = pVision && (pVision.IDVision || (pVision.Record && pVision.Record.IDVision));
				if (!tmpChildID) { throw new Error('vision create returned no id'); }
				return this.pict.providers.LinkAPI.addLink('VisionVisionJoin',
					{ IDVisionSource: tmpParentID, IDVisionTarget: tmpChildID, RelationType: 'Decomposes' });
			})
			.then(() => { this._pending['svn' + pIdx] = ''; this._toast('Sub-vision created.', 'success'); this.showDetail(this._key, this._id); })
			.catch((pErr) => this._toast('Could not create sub-vision: ' + (pErr.message || pErr), 'error'));
	}

	addSubVision(pIdx)
	{
		let tmpChildID = this._pending['svl' + pIdx];
		if (!tmpChildID) { return this._toast('Pick a vision to add as a part.', 'info'); }
		this.pict.providers.LinkAPI.addLink('VisionVisionJoin',
			{ IDVisionSource: this._id, IDVisionTarget: Number(tmpChildID), RelationType: 'Decomposes' })
			.then(() => { this._pending['svl' + pIdx] = null; this.showDetail(this._key, this._id); })
			.catch((pErr) => this._toast('Link failed: ' + (pErr.message || pErr), 'error'));
	}

	removeSubVision(pIdx, pJoinID)
	{
		this.pict.providers.LinkAPI.removeLink('VisionVisionJoin', pJoinID)
			.then(() => this.showDetail(this._key, this._id))
			.catch((pErr) => this._toast('Unlink failed: ' + (pErr.message || pErr), 'error'));
	}

	// A create-from-here button on an inverse-fk section: open the source entity's
	// create form prefilled with this record's FK plus the relation's Prefill.
	createFromRelation(pIdx)
	{
		let tmpRel = libDescriptors.Entities[this._key].Relations[pIdx];
		if (!tmpRel || !tmpRel.Create) { return; }
		let tmpPrefill = Object.assign({}, tmpRel.Create.Prefill || {});
		tmpPrefill[tmpRel.SourceField] = this._id;
		let tmpApp = this.pict.PictApplication;
		if (tmpRel.Source === 'Feedback' && tmpApp && typeof tmpApp.showNewFeedback === 'function')
		{
			return tmpApp.showNewFeedback(tmpPrefill);
		}
		this.pict.AppData.EntityListPrefill = { Key: tmpRel.Source, Values: tmpPrefill };
		// The create route, so the URL says a form is open rather than naming the list behind it.
		if (tmpApp && typeof tmpApp.showEntityNew === 'function' && tmpApp.showEntityNew(tmpRel.Source)) { return; }
		if (tmpApp && typeof tmpApp.showSection === 'function') { tmpApp.showSection(tmpRel.Source); }
	}

	// Dispatch a descriptor-declared header action (HeaderActions) to the app.
	headerAction(pMethod)
	{
		let tmpApp = this.pict.PictApplication;
		if (tmpApp && typeof tmpApp[pMethod] === 'function') { tmpApp[pMethod](this._key, this._id); }
	}

	// Mark this record addressed / reopen it (Addressable entities), stamping or clearing
	// the date, then reload the detail so the badge and toggle reflect the new state.
	toggleAddressed()
	{
		let tmpDesc = libDescriptors.Entities[this._key];
		let tmpAddr = tmpDesc && tmpDesc.Addressable;
		let tmpProvider = this._provider(this._key);
		if (!tmpAddr || !tmpProvider) { return; }
		let tmpRecord = tmpProvider.store().Index[this._id] || {};
		let tmpNext = libFocus.truthy(tmpRecord[tmpAddr.Field]) ? 0 : 1;
		let tmpPatch = {};
		tmpPatch[tmpAddr.Field] = tmpNext;
		if (tmpAddr.DateField) { tmpPatch[tmpAddr.DateField] = tmpNext ? (new Date()).toISOString() : ''; }
		tmpProvider.update(this._id, tmpPatch)
			.then(() => this.showDetail(this._key, this._id))
			.catch((pErr) => { if (this.pict && this.pict.log) { this.pict.log.warn('EntityDetail: address toggle failed: ' + (pErr.message || pErr)); } });
	}

	// ── navigation ───────────────────────────────────────────────────────────
	openTarget(pKey, pID)
	{
		let tmpApp = this.pict.PictApplication;
		if (tmpApp && typeof tmpApp.showEntityDetail === 'function') tmpApp.showEntityDetail(pKey, pID);
	}

	// Open a stored session in its viewer (the #/session/:id route), used by the Story panel.
	openSession(pID)
	{
		let tmpApp = this.pict.PictApplication;
		if (tmpApp && typeof tmpApp.showSession === 'function') tmpApp.showSession(pID);
	}

	back()
	{
		let tmpApp = this.pict.PictApplication;
		if (tmpApp && typeof tmpApp.showSection === 'function') tmpApp.showSection(this._key);
	}

	edit()
	{
		let tmpKey = this._key, tmpID = this._id;
		let tmpList = this.pict.views['Plansheet-EntityList'];
		// Render the GENERIC entity list (not the entity's section): an Issue's section is the kanban
		// board, which has no inline edit form, so showSection('Issue') used to bounce edits to the board.
		// Hand the id to showEntity so it points the list at this entity, loads it once, and opens the
		// edit form as the final render. A separate list()+editRecord used to race showEntity's own list
		// load; for entities with a facet prefetch (WorkItem's roster) the gated list won and the edit
		// form flickered and was clobbered back to the list.
		//
		// NAVIGATES now. Calling showEntity straight from here left the URL on /work-item/42 while the
		// pane held the edit form, and on /work-item/42 again after Cancel while the whole list was on
		// screen -- the address bar disagreeing with the page in both directions. The route handler
		// makes the same showEntity call, so the render path below is unchanged.
		let tmpApp = this.pict.PictApplication;
		if (tmpApp && typeof tmpApp.showEntityEdit === 'function' && tmpApp.showEntityEdit(tmpKey, tmpID)) { return; }
		if (tmpList && typeof tmpList.showEntity === 'function') { tmpList.showEntity(tmpKey, { EditID: tmpID }); }
	}

	// Soft-delete this record (descriptor Deletable: true -- WorkItem) from its own full
	// view, so a person reviewing the content can decide right there instead of hunting
	// the row down in the list. meadow's delete just sets Deleted/DeleteDate; the
	// WorkItemNumber sequence ignores Deleted rows, so numbering is unaffected.
	deleteRecord()
	{
		let tmpKey = this._key, tmpID = this._id;
		let tmpDesc = libDescriptors.Entities[tmpKey];
		if (!tmpDesc || !tmpDesc.Deletable) { return; }
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		let tmpSingular = libLabels.singular(this.pict, tmpKey, this._scopeTerms, tmpDesc.Title.replace(/s$/, ''));
		let tmpDo = () =>
		{
			let tmpProvider = this._provider(tmpKey);
			if (!tmpProvider) { return; }
			tmpProvider.remove(tmpID)
				.then(() => tmpProvider.list())
				.then(() =>
				{
					this._toast(tmpSingular + ' deleted.', 'success');
					let tmpApp = this.pict.PictApplication;
					if (tmpApp && typeof tmpApp.showSection === 'function') { tmpApp.showSection(tmpKey); }
				})
				.catch((pErr) => this._toast('Delete failed: ' + ((pErr && pErr.message) || pErr), 'error'));
		};
		let tmpMsg = 'Delete this ' + tmpSingular.toLowerCase() + '? This cannot be undone from here.';
		if (tmpModal && typeof tmpModal.confirm === 'function')
		{
			tmpModal.confirm(tmpMsg, { title: 'Delete ' + tmpSingular.toLowerCase(), dangerous: true, confirmLabel: 'Delete' }).then((pOk) => { if (pOk) { tmpDo(); } });
		}
		else { tmpDo(); }
	}

	_toast(pMessage, pType)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && typeof tmpModal.toast === 'function')
		{
			tmpModal.toast(pMessage, { type: pType || 'info', duration: (pType === 'error') ? 6000 : 2500 });
		}
	}

	// ── Blueprint title block (WorkItem) ──────────────────────────────────────
	// Draw the loaded blueprint into its container: one drafting cell per artifact
	// type with its count and items, labels in the work item's vision kit. Clicking
	// an item navigates to it. Re-runs every render (the container is rebuilt each).
	_renderBlueprint()
	{
		if (typeof document === 'undefined') { return; }
		let tmpHost = document.getElementById('Entity-Blueprint');
		if (!tmpHost) { return; }
		let tmpBP = this._blueprint;
		if (!tmpBP || !Array.isArray(tmpBP.Groups)) { tmpHost.innerHTML = ''; return; }
		let tmpSelf = "_Pict.views['Plansheet-EntityDetail']";
		let tmpApp = this.pict.PictApplication;
		let tmpCanEdit = !!(tmpApp && typeof tmpApp._can === 'function' && tmpApp._can('content.edit'));
		let tmpCells = tmpBP.Groups.map((pGroup) =>
		{
			let tmpItems = (pGroup.Items || []).map((pItem) =>
			{
				let tmpClick = pItem.Link ? ' onclick="' + tmpSelf + '.openBlueprintLink(\'' + pItem.Link + '\')"' : '';
				// A pinned artifact carries its pin id and gets an unpin control (editors only).
				let tmpX = (tmpCanEdit && pItem.Pinned && pItem.IDBlueprintItem)
					? '<button class="bp-item-x" title="Unpin" onclick="event.stopPropagation();' + tmpSelf + '.unpinBlueprintItem(' + pItem.IDBlueprintItem + ')">&times;</button>'
					: '';
				return '<span class="bp-item' + (pItem.Pinned ? ' is-pinned' : '') + '"' + tmpClick + ' title="' + _escapeHTML(pItem.Title || '') + '">' + _escapeHTML(pItem.Title || '') + tmpX + '</span>';
			}).join('');
			return '<div class="bp-cell">' +
					'<div class="bp-cell-head"><span class="bp-count">' + (pGroup.Count || 0) + '</span><span class="bp-label">' + _escapeHTML(pGroup.Label || pGroup.Type) + '</span></div>' +
					'<div class="bp-items">' + tmpItems + '</div>' +
				'</div>';
		}).join('');
		if (!tmpCells) { tmpCells = '<div class="bp-empty">Nothing linked yet. As this work item gathers its parts, they appear here.</div>'; }
		// _canShare(), not tmpCanEdit: being able to edit this entity is not the same as this entity being
		// shareable at all. Public sharing is work-item-only (see _canShare), and the share routes take a bare
		// :id, so drawing this strip on a module would offer a button that publishes the WORK ITEM of the same
		// number. Blueprints generalized in WI #340; sharing did not.
		let tmpShareHTML = this._canShare() ? libShareUI.stripHTML(this._share, tmpSelf) : '';
		tmpHost.innerHTML = tmpShareHTML +
			'<div class="bp-sheet"><div class="bp-frame">' +
				'<div class="bp-head"><span class="bp-kicker">BLUEPRINT</span><span class="bp-title">' + _escapeHTML(tmpBP.Title || '') + '</span>' +
					'<a class="bp-open" href="#/' + this._routeKey() + '/' + this._id + '/blueprint" style="margin-left:auto;align-self:center;font:inherit;font-size:12px;letter-spacing:.4px;color:#f0c44a;text-decoration:none;border:1px solid #f0c44a;border-radius:6px;padding:4px 11px;white-space:nowrap;">Open full blueprint</a>' +
				'</div>' +
				'<div class="bp-grid">' + tmpCells + '</div>' +
				this._blueprintAddHTML(tmpCanEdit, tmpSelf) +
				'<div class="bp-titleblock">' +
					'<span class="bp-tb-cell"><span class="bp-tb-k">SHEET</span><span class="bp-tb-v">' + (tmpBP.Designator || ('WI-' + (tmpBP.WorkItemNumber || tmpBP.IDWorkItem || 0))) + '</span></span>' +
					'<span class="bp-tb-cell"><span class="bp-tb-k">STATUS</span><span class="bp-tb-v">' + _escapeHTML(tmpBP.Status || '-') + '</span></span>' +
				'</div>' +
			'</div></div>';
		// Mount the two searchable pickers into the composer hosts (editors only). Re-runs on every
		// _renderBlueprint (including a type change), so the item options track the chosen type.
		if (tmpCanEdit && document.getElementById('bp-add-item-host'))
		{
			let tmpTypes = ['Vision', 'Module', 'Feature'];
			let tmpAddType = (tmpTypes.indexOf(this._bpAddType) >= 0) ? this._bpAddType : 'Vision';
			let tmpSelfView = this;
			// Type selector: a pict-section-picker (matching the item picker), not a native <select>. The
			// guard keeps setBlueprintAddType -> _renderBlueprint -> re-mount from looping when setValue
			// re-asserts the current type.
			if (tmpApp && typeof tmpApp.mountPicker === 'function' && document.getElementById('bp-add-type-host'))
			{
				let tmpTypeOptions = tmpTypes.map((pT) => ({ Value: pT, Text: libLabels.singular(this.pict, pT, this._scopeTerms, pT) }));
				tmpApp.mountPicker('#bp-add-type-host', 'BP-AddTypePicker', tmpTypeOptions,
					function (pVal) { if (pVal && pVal !== tmpSelfView._bpAddType) { tmpSelfView.setBlueprintAddType(pVal); } },
					{ Placeholder: 'Type', Value: tmpAddType });
			}
			// Item selector: a server-backed candidate search (WI #445), not the chosen type's cached
			// page -- that page is at most 250 rows (WI #433) and prod holds 1,075 blueprints. See
			// CandidateSearch.js for why this asks resolve(), not list(), and only the TitleField.
			let tmpAddDesc = libDescriptors.Entities[tmpAddType];
			let tmpAddProvider = this._provider(tmpAddType);
			if (tmpApp && typeof tmpApp.mountPicker === 'function' && tmpAddDesc && tmpAddProvider)
			{
				// Preserve any pending selection across an incidental re-render (async data settling, a
				// board mount): re-assert it as the picker Value so a repaint can't silently clear the
				// item the user just chose and leave the Pin it click with nothing to pin.
				tmpApp.mountPicker('#bp-add-item-host', 'BP-AddItemPicker', [], function (pVal) { tmpSelfView._bpSelectedItem = parseInt(pVal, 10) || 0; },
					{
						Placeholder: 'Search ' + tmpAddType.toLowerCase() + 's to pin',
						Value: this._bpSelectedItem ? String(this._bpSelectedItem) : '',
						DataProvider: libCandidateSearch.dataProviderFor(tmpAddProvider, tmpAddDesc),
						ResolveValue: libCandidateSearch.resolveValueFor(tmpAddProvider, tmpAddDesc)
					});
			}
		}
	}

	// ── Blueprint public-share strip (editors) ────────────────────────────────
	// The compact block carries the same share control as the full-page view, via
	// BlueprintShareUI. It operates on this view (_id, _share); _afterShareChange
	// redraws just the blueprint block so a toggle does not re-fetch the entity.
	// May this entity's blueprint be shared publicly? Any blueprintable entity can be, now that a share hangs
	// off IDBlueprint rather than IDWorkItem and the public read, the page and the token-scoped media all
	// resolve through the blueprint's own owner (WI #340). The gate is the editing right, as it was.
	_canShare()
	{
		if (!libDescriptors.Entities[this._key] || !libDescriptors.Entities[this._key].Blueprint) { return false; }
		let tmpApp = this.pict.PictApplication;
		return !!(tmpApp && typeof tmpApp._can === 'function' && tmpApp._can('content.edit'));
	}
	// The hash-route segment for this entity ('work-item', 'vision', ...), so the full-blueprint link points
	// at the page for the thing actually open rather than always at a work item.
	_routeKey()
	{
		let tmpApp = this.pict.PictApplication;
		if (tmpApp && typeof tmpApp._singularRoute === 'function') { return tmpApp._singularRoute(this._key); }
		return String(this._key || '').toLowerCase();
	}

	_bpAPI() { return this.pict.providers.BlueprintAPI; }

	// Which subject THIS entity's pins are written against. The work-item asymmetry (its pins are keyed
	// by IDWorkItem, everything else by IDBlueprint) lives in the provider, so this view no longer
	// carries a special case for it.
	_curationSubject() { return this._bpAPI().curationSubject(this._blueprint, this._subject); }

	_afterShareChange() { this._renderBlueprint(); }
	bpShareCreate() { libShareUI.create(this); }
	bpShareCopy() { libShareUI.copy(this); }
	bpShareInvite() { libShareUI.invite(this); }
	bpShareRegenerate() { libShareUI.regenerate(this); }
	bpShareRevoke() { libShareUI.revoke(this); }
	bpShareSetExpiry(pValue) { libShareUI.setExpiry(this, pValue); }
	bpSharePrintEnabled(pEnabled) { libShareUI.setPrintEnabled(this, pEnabled); }
	bpShareRepin() { libShareUI.repin(this); }

	// The "pin another artifact" row at the foot of the blueprint (editors only): pick a type, pick
	// one of that type, and pin it. Types are the loaded SDLC bases (their lists are already cached on
	// the work item page); the server title-resolves them. Pins persist; auto-gathered parts do not.
	_blueprintAddHTML(pCanEdit, pSelf)
	{
		if (!pCanEdit) { return ''; }
		// One framed panel, not three loose inputs. BOTH selectors are searchable pict-section-pickers
		// (mounted after render into the two hosts, and themed to the blueprint's navy palette via the
		// token re-map on .bp-add), so the type and item selectors match and read as one deliberate control.
		return '<div class="bp-add">' +
				'<span class="bp-add-kicker">PIN AN ARTIFACT</span>' +
				'<div class="bp-add-row">' +
					'<div class="bp-add-field bp-add-type" id="bp-add-type-host"></div>' +
					'<div class="bp-add-field bp-add-item" id="bp-add-item-host"></div>' +
					'<button class="bp-add-btn" onclick="' + pSelf + '.pinBlueprintItem()">Pin it</button>' +
				'</div>' +
			'</div>';
	}

	// Open the selected whiteboard full screen (read-only; a photo board shows full-bleed).
	fullscreenBoard()
	{
		let tmpApp = this.pict.PictApplication;
		if (tmpApp && this._SelectedBoardID && typeof tmpApp.openBoardFullscreen === 'function') { tmpApp.openBoardFullscreen(this._SelectedBoardID); }
	}

	// Switching the artifact type invalidates any chosen item (a Feature id is meaningless once the
	// type is Vision), so clear the pending selection before repainting the linker for the new type.
	setBlueprintAddType(pType) { this._bpAddType = pType; this._bpSelectedItem = 0; this._renderBlueprint(); }

	pinBlueprintItem()
	{
		if (typeof document === 'undefined') { return; }
		let tmpType = this._bpAddType || 'Vision';
		// Read the chosen item from the picker itself (the source of truth) rather than a cached
		// instance field: an async re-render can repaint the linker between the pick and this click,
		// so reading the live picker value keeps Pin it working instead of silently no-opping.
		let tmpItemPicker = this.pict.views['BP-AddItemPicker'];
		let tmpIDItem = (tmpItemPicker && typeof tmpItemPicker.getValue === 'function') ? (parseInt(tmpItemPicker.getValue(), 10) || 0) : 0;
		if (!tmpIDItem) { tmpIDItem = this._bpSelectedItem || 0; }
		if (!tmpIDItem) { return; }
		this._bpAPI().pin(this._curationSubject(), tmpType, tmpIDItem)
			.then(() => { this._bpSelectedItem = 0; return this._reloadBlueprint(); })
			.catch((pError) => { if (this.pict && this.pict.log) { this.pict.log.warn('Could not pin the artifact: ' + pError.message); } });
	}

	unpinBlueprintItem(pIDBlueprintItem)
	{
		if (!pIDBlueprintItem) { return; }
		this._bpAPI().unpin(this._curationSubject(), pIDBlueprintItem)
			.then(() => this._reloadBlueprint())
			.catch((pError) => { if (this.pict && this.pict.log) { this.pict.log.warn('Could not unpin: ' + pError.message); } });
	}

	// Re-fetch just the blueprint and redraw it (cheaper than reloading the whole detail page).
	_reloadBlueprint()
	{
		return this._bpAPI().readSubject(this._subject)
			.then((pBody) => { this._blueprint = pBody || null; this._renderBlueprint(); })
			.catch(() => {});
	}

	openBlueprintLink(pLink)
	{
		let tmpApp = this.pict.PictApplication;
		if (tmpApp && typeof tmpApp._navigate === 'function' && pLink) { tmpApp._navigate(pLink); }
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap) { this.pict.CSSMap.injectCSS(); }
		this._renderCoreDoc();
		this._renderMarkdownFields();
		this._mountVisionBoard();
		this._mountComments();
		this._mountChecklists();
		this._mountVisionLoops();
		this._mountStepEditor();
		this._mountPrompts();
		this._renderBlueprint();
		this._mountConnections();
		this._mountWorkItemPrompts();
		this._mountRelationPickers();
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}

	// ── relation record pickers (WI #446) ─────────────────────────────────────
	// Mount the server-backed candidate-search pickers for this entity's fk / link-add / target-entity /
	// subvisions-add relation slots, into the host divs _buildSection() just rendered. Re-runs on every
	// render, same as the other onAfterRender mounts above: the relation Sections rebuild from scratch on
	// every _rebuild (a link/unlink, an fk change, a target-type switch), so there is nothing to diff --
	// each host is either present (mount fresh into it) or absent (that relation isn't on screen).
	_mountRelationPickers()
	{
		let tmpApp = this.pict.PictApplication;
		if (!tmpApp || typeof tmpApp.mountPicker !== 'function' || typeof document === 'undefined') { return; }
		let tmpDesc = libDescriptors.Entities[this._key];
		let tmpRecord = this._loadedRecord || {};
		let tmpRelations = (tmpDesc && tmpDesc.Relations) || [];
		let tmpSelfView = this;
		let fLabel = (pKey) => { let tmpD = libDescriptors.Entities[pKey]; return ((tmpD && tmpD.Title) || pKey).toLowerCase(); };
		tmpRelations.forEach((pRel, pIdx) =>
		{
			if (pRel.Render === 'fk')
			{
				let tmpHostID = 'rel-fk-host-' + pIdx;
				if (!document.getElementById(tmpHostID)) { return; }
				let tmpTargetDesc = libDescriptors.Entities[pRel.Target];
				let tmpProvider = this._provider(pRel.Target);
				if (!tmpTargetDesc || !tmpProvider) { return; }
				let tmpCurrent = tmpRecord[pRel.Field] || 0;
				tmpApp.mountPicker('#' + tmpHostID, 'Rel-Fk-' + this._key + '-' + pIdx, [],
					function (pVal) { tmpSelfView.setFk(pRel.Field, pVal); },
					{
						Placeholder: 'Search ' + fLabel(pRel.Target),
						Value: tmpCurrent ? String(tmpCurrent) : '',
						AllowClear: true, ClearLabel: '— none —',
						DataProvider: libCandidateSearch.dataProviderFor(tmpProvider, tmpTargetDesc),
						ResolveValue: libCandidateSearch.resolveValueFor(tmpProvider, tmpTargetDesc)
					});
			}
			else if (pRel.Render === 'link')
			{
				let tmpHostID = 'rel-link-add-host-' + pIdx;
				if (!document.getElementById(tmpHostID)) { return; }
				let tmpTargetDesc = libDescriptors.Entities[pRel.Target];
				let tmpProvider = this._provider(pRel.Target);
				if (!tmpTargetDesc || !tmpProvider) { return; }
				let tmpLinkedIDs = (this._linkRows[pIdx] || []).map((pR) => String(pR[pRel.TargetField]));
				if (pRel.Target === this._key) { tmpLinkedIDs.push(String(this._id)); }
				let tmpPendingTarget = (this._pending[pIdx] && this._pending[pIdx].Target) || '';
				tmpApp.mountPicker('#' + tmpHostID, 'Rel-Link-' + this._key + '-' + pIdx, [],
					function (pVal) { tmpSelfView.setPendingTarget(pIdx, pVal); },
					{
						Placeholder: 'Search ' + fLabel(pRel.Target) + ' to link',
						Value: tmpPendingTarget ? String(tmpPendingTarget) : '',
						DataProvider: libCandidateSearch.dataProviderFor(tmpProvider, tmpTargetDesc, null, tmpLinkedIDs),
						ResolveValue: libCandidateSearch.resolveValueFor(tmpProvider, tmpTargetDesc)
					});
			}
			else if (pRel.Render === 'target')
			{
				let tmpHostID = 'rel-target-host-' + pIdx;
				if (!document.getElementById(tmpHostID)) { return; }
				let tmpDraftType = this._targetType || tmpRecord.TargetType || 'None';
				let tmpTargetDesc = libDescriptors.Entities[tmpDraftType];
				let tmpProvider = (tmpDraftType !== 'None') ? this._provider(tmpDraftType) : null;
				if (!tmpTargetDesc || !tmpProvider) { return; }
				let tmpCurID = tmpRecord['IDTarget' + tmpDraftType] || 0;
				tmpApp.mountPicker('#' + tmpHostID, 'Rel-Target-' + this._key + '-' + pIdx, [],
					function (pVal) { tmpSelfView.setTarget(pVal); },
					{
						Placeholder: 'Search ' + fLabel(tmpDraftType),
						Value: tmpCurID ? String(tmpCurID) : '',
						AllowClear: true, ClearLabel: '— none —',
						DataProvider: libCandidateSearch.dataProviderFor(tmpProvider, tmpTargetDesc),
						ResolveValue: libCandidateSearch.resolveValueFor(tmpProvider, tmpTargetDesc)
					});
			}
			else if (pRel.Render === 'subvisions')
			{
				let tmpHostID = 'rel-subv-host-' + pIdx;
				if (!document.getElementById(tmpHostID)) { return; }
				let tmpProvider = this._provider('Vision');
				if (!tmpProvider) { return; }
				let tmpSub = this._subRows[pIdx] || { Out: [], In: [] };
				let tmpExcludeIDs = (tmpSub.Out || [])
					.filter((pR) => String(pR.RelationType) === 'Decomposes')
					.map((pR) => String(pR.IDVisionTarget));
				tmpExcludeIDs.push(String(this._id));
				let tmpPendingSub = this._pending['svl' + pIdx] || '';
				tmpApp.mountPicker('#' + tmpHostID, 'Rel-SubVision-' + this._key + '-' + pIdx, [],
					function (pVal) { tmpSelfView.setPendingSubLink(pIdx, pVal); },
					{
						Placeholder: 'Search visions to add as a part',
						Value: tmpPendingSub ? String(tmpPendingSub) : '',
						DataProvider: libCandidateSearch.dataProviderFor(tmpProvider, libDescriptors.Entities.Vision, null, tmpExcludeIDs),
						ResolveValue: libCandidateSearch.resolveValueFor(tmpProvider, libDescriptors.Entities.Vision)
					});
			}
		});
	}

	// ── embedded inspiration board (Vision) ───────────────────────────────────

	// The current board's IDMoodboard, read by the edit board's Media image source to scope its gallery.
	currentBoardId() { return this._VisionBoardID; }

	// Mount (or re-mount) the embedded board after a render. The detail re-renders on relation edits,
	// which wipes the #Vision-Board container, so this runs every render: if the board for this entity is
	// already loaded it just re-mounts the cached board (no server round-trip), otherwise it fetches it
	// (creating one when the user can edit) and caches it.
	_mountVisionBoard()
	{
		let tmpState = this.pict.AppData.EntityDetail;
		let tmpSlot = (tmpState && tmpState.BoardSlot && tmpState.BoardSlot[0]) ? tmpState.BoardSlot[0] : null;
		// Any path that does not mount an editable board must clear the editing flag. Vision-Board-Edit is a
		// reused singleton; a stale _VisionBoardEditing left true (e.g. navigating away mid-edit, then to an
		// entity with no board) would later make the cache-hit re-render below open the board in edit mode.
		if (!tmpSlot) { this._VisionBoardEditing = false; return; }
		let tmpAPI = this.pict.providers.MoodboardAPI;
		if (!tmpAPI || typeof document === 'undefined') { this._VisionBoardEditing = false; return; }
		let tmpBoards = this._ownerBoards || [];
		let tmpSelected = Number(this._SelectedBoardID) || (tmpBoards[0] && tmpBoards[0].IDMoodboard) || 0;
		this._SelectedBoardID = tmpSelected;
		let tmpHost = document.getElementById('Vision-Board');
		// No boards yet: the template shows an empty state, nothing to mount -- collapse the box so no empty
		// bordered rectangle sits under the "none yet" text.
		if (!tmpSelected) { this._VisionBoardEditing = false; if (tmpHost) { tmpHost.innerHTML = ''; tmpHost.classList.add('ps-board-empty-collapsed'); } return; }
		let tmpCacheKey = this._key + ':' + this._id + ':' + tmpSelected;
		if (this._VisionBoardLoadedFor === tmpCacheKey && this._VisionBoardID)
		{
			this._renderVisionBoardView(this._VisionBoardEditing && tmpSlot.CanEdit, this._VisionBoard);
			return;
		}
		let tmpRow = tmpBoards.find((pB) => Number(pB.IDMoodboard) === tmpSelected);
		if (!tmpRow) { this._VisionBoardEditing = false; if (tmpHost) { tmpHost.innerHTML = ''; tmpHost.classList.add('ps-board-empty-collapsed'); } return; }
		this._VisionBoardID = tmpRow.IDMoodboard;
		this._VisionBoard = tmpAPI.loadBoard(tmpRow);
		// A freshly created board opens straight in edit (WantBoardEdit); otherwise read-only.
		this._VisionBoardEditing = !!(this._WantBoardEdit && tmpSlot.CanEdit);
		this._WantBoardEdit = false;
		this._VisionBoardLoadedFor = tmpCacheKey;
		this._renderVisionBoardView(this._VisionBoardEditing, this._VisionBoard);
	}

	// Switch the mounted whiteboard (pill click).
	/**
	 * Pick which of this entity's whiteboards is mounted in its board section.
	 *
	 * Rides in the entity's own URL as ?board=34 rather than a route of its own: it is a refinement of
	 * the record you are already looking at, not a different page. Without it, an entity with several
	 * boards always reopened on the first one, so "the board I was looking at" could not be linked or
	 * returned to.
	 */
	selectBoard(pID)
	{
		let tmpID = Number(pID) || 0;
		if (!tmpID || tmpID === Number(this._SelectedBoardID)) { return; }
		this._SelectedBoardID = tmpID;
		this._VisionBoardEditing = false;
		this._VisionBoardLoadedFor = null;
		libFocus.writeHashQuery({ board: tmpID });
		this._rebuild();
	}

	// Add a new whiteboard for this entity, select it, and drop straight into edit.
	newBoard()
	{
		let tmpApp = this.pict.PictApplication;
		if (!tmpApp || typeof tmpApp._can !== 'function' || !tmpApp._can('content.edit')) { return; }
		let tmpAPI = this.pict.providers.MoodboardAPI;
		if (!tmpAPI) { return; }
		let tmpName = 'Whiteboard ' + ((this._ownerBoards || []).length + 1);
		tmpAPI.createForOwner(this._key, this._id, tmpName).then((pRow) =>
		{
			if (!pRow || !pRow.IDMoodboard) { return; }
			return tmpAPI.listForEntity(this._key, this._id).then((pRows) =>
			{
				this._ownerBoards = Array.isArray(pRows) ? pRows : [];
				this._SelectedBoardID = pRow.IDMoodboard;
				this._VisionBoardLoadedFor = null;
				this._WantBoardEdit = true;
				this._rebuild();
			});
		}).catch(() => {});
	}

	// Open the hidden board-photo file input ("+ Photo").
	pickBoardPhoto()
	{
		if (typeof document === 'undefined') { return; }
		let tmpInput = document.getElementById('Entity-Board-PhotoInput');
		if (tmpInput) { tmpInput.value = ''; tmpInput.click(); }
	}

	onBoardPhotoPicked(pInput)
	{
		let tmpFile = pInput && pInput.files && pInput.files[0];
		if (tmpFile) { this.newBoardFromPhoto(tmpFile); }
	}

	// Add a whiteboard for this entity whose content is a photo (in-person capture). Creates the board,
	// opens it in edit, and drops the image in once the editor mounts (via _PendingPhoto).
	newBoardFromPhoto(pFile)
	{
		let tmpApp = this.pict.PictApplication;
		if (!tmpApp || typeof tmpApp._can !== 'function' || !tmpApp._can('content.edit') || !pFile) { return; }
		let tmpAPI = this.pict.providers.MoodboardAPI;
		if (!tmpAPI) { return; }
		let tmpName = (pFile.name || 'Photo').replace(/\.[^.]+$/, '').slice(0, 80) || 'Photo';
		tmpAPI.createForOwner(this._key, this._id, tmpName).then((pRow) =>
		{
			if (!pRow || !pRow.IDMoodboard) { return; }
			return tmpAPI.listForEntity(this._key, this._id).then((pRows) =>
			{
				this._ownerBoards = Array.isArray(pRows) ? pRows : [];
				this._SelectedBoardID = pRow.IDMoodboard;
				this._VisionBoardLoadedFor = null;
				this._WantBoardEdit = true;
				this._PendingPhoto = pFile;
				this._rebuild();
			});
		}).catch(() => {});
	}

	// ── attach an existing whiteboard ─────────────────────────────────────────

	// A human label for where a candidate board currently lives, or null when it is standalone (no owner).
	// Mirrors the Whiteboards page's owner label so the attach picker reads the same way.
	_boardOwnerInfo(pRow)
	{
		if (!pRow) { return null; }
		if (pRow.OwnerType === 'Vision' || pRow.IDVision) { return { Label: 'Vision #' + (pRow.IDOwner || pRow.IDVision) }; }
		if (pRow.OwnerType === 'WorkItem') { return { Label: 'Work Item #' + pRow.IDOwner }; }
		if (pRow.OwnerType === 'Feature') { return { Label: 'Feature #' + pRow.IDOwner }; }
		if (pRow.OwnerType === 'Module') { return { Label: 'Module #' + pRow.IDOwner }; }
		if (pRow.OwnerType === 'User' || pRow.IDUser) { return { Label: 'a profile' }; }
		if (pRow.OwnerType) { return { Label: pRow.OwnerType + ' #' + pRow.IDOwner }; }
		return null;
	}

	// "+ Attach": pick an existing whiteboard (standalone, or one attached to another entity) and point it
	// at this entity. Loads the tenant's boards, drops the ones already here, and shows the rest in an
	// anchored menu with a hint of where each currently lives. Editors only.
	attachExistingBoard(pAnchor)
	{
		let tmpApp = this.pict.PictApplication;
		if (!tmpApp || typeof tmpApp._can !== 'function' || !tmpApp._can('content.edit')) { return; }
		let tmpAPI = this.pict.providers.MoodboardAPI;
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (!tmpAPI || !tmpModal || typeof tmpModal.dropdown !== 'function') { return; }
		tmpAPI.listAllForCustomer().then((pRows) =>
		{
			let tmpHere = {};
			(this._ownerBoards || []).forEach((pB) => { tmpHere[Number(pB.IDMoodboard)] = true; });
			let tmpCandidates = (Array.isArray(pRows) ? pRows : []).filter((pB) => pB && !tmpHere[Number(pB.IDMoodboard)]);
			// Standalone boards first (the common case: attach the field-trial sketch), then boards that
			// currently live elsewhere (re-association), each labelled with where they are.
			tmpCandidates.sort((pA, pB) =>
			{
				let tmpAOwned = this._boardOwnerInfo(pA) ? 1 : 0;
				let tmpBOwned = this._boardOwnerInfo(pB) ? 1 : 0;
				if (tmpAOwned !== tmpBOwned) { return tmpAOwned - tmpBOwned; }
				return String(pA.Name || '').localeCompare(String(pB.Name || ''));
			});
			let tmpItems = [{ Header: 'Attach an existing whiteboard' }];
			if (!tmpCandidates.length)
			{
				tmpItems.push({ Hash: '', Label: 'No other whiteboards to attach', Disabled: true, Tooltip: 'Draw one on the Whiteboards page first, then attach it here.' });
			}
			else
			{
				tmpCandidates.forEach((pB) =>
				{
					let tmpOwner = this._boardOwnerInfo(pB);
					tmpItems.push({ Hash: String(pB.IDMoodboard), Label: pB.Name || ('Whiteboard #' + pB.IDMoodboard), Hint: tmpOwner ? tmpOwner.Label : 'Standalone' });
				});
			}
			tmpModal.dropdown(pAnchor || '#Vision-Board-Menu-Btn', { align: 'left', minWidth: '240px', items: tmpItems }).then((pChoice) =>
			{
				if (!pChoice || !pChoice.Hash) { return; }
				let tmpID = Number(pChoice.Hash) || 0;
				if (!tmpID) { return; }
				let tmpRow = tmpCandidates.find((pB) => Number(pB.IDMoodboard) === tmpID);
				let tmpOwner = this._boardOwnerInfo(tmpRow);
				if (tmpOwner && tmpModal && typeof tmpModal.confirm === 'function')
				{
					// Attached elsewhere: attaching moves it. Say so before re-pointing it.
					tmpModal.confirm('This whiteboard is currently on ' + tmpOwner.Label + '. Attach it here and remove it from there?', { title: 'Move whiteboard here', confirmLabel: 'Attach here' })
						.then((pOk) => { if (pOk) { this._attachBoard(tmpID); } });
				}
				else { this._attachBoard(tmpID); }
			});
		}).catch((pErr) => this._toast('Could not load whiteboards to attach: ' + ((pErr && pErr.message) || pErr), 'error'));
	}

	// Re-point a board at this entity, then reload the section and drop straight into it (read-only mount).
	_attachBoard(pID)
	{
		let tmpAPI = this.pict.providers.MoodboardAPI;
		if (!tmpAPI || !pID) { return; }
		tmpAPI.attachToOwner(pID, this._key, this._id).then(() =>
		{
			return tmpAPI.listForEntity(this._key, this._id).then((pRows) =>
			{
				this._ownerBoards = Array.isArray(pRows) ? pRows : [];
				this._SelectedBoardID = Number(pID);
				this._VisionBoardEditing = false;
				this._VisionBoardLoadedFor = null;
				this._rebuild();
				this._toast('Whiteboard attached.', 'success');
			});
		}).catch((pErr) => this._toast('Attach failed: ' + ((pErr && pErr.message) || pErr), 'error'));
	}

	// ── manage the selected whiteboard (rename / disconnect / delete) ─────────

	// Open the actions menu for the selected board, anchored to the kebab button. Editors only. The
	// menu acts on the currently selected (mounted) board -- the one whose pill is active.
	boardMenu(pAnchor)
	{
		let tmpApp = this.pict.PictApplication;
		if (!tmpApp || typeof tmpApp._can !== 'function' || !tmpApp._can('content.edit')) { return; }
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		let tmpID = Number(this._SelectedBoardID) || 0;
		if (!tmpModal || typeof tmpModal.dropdown !== 'function' || !tmpID) { return; }
		let tmpRow = (this._ownerBoards || []).find((pB) => Number(pB.IDMoodboard) === tmpID);
		let tmpName = (tmpRow && tmpRow.Name) || ('Whiteboard #' + tmpID);
		tmpModal.dropdown(pAnchor || '#Vision-Board-Menu-Btn',
		{
			align: 'right',
			minWidth: '200px',
			items:
			[
				{ Header: tmpName },
				{ Hash: 'rename', Label: 'Rename…' },
				{ Hash: 'disconnect', Label: 'Disconnect from here' },
				{ Separator: true },
				{ Hash: 'delete', Label: 'Delete', Style: 'danger' }
			]
		}).then((pChoice) =>
		{
			if (!pChoice || !pChoice.Hash) { return; }
			if (pChoice.Hash === 'rename') { this._renameBoard(tmpID); }
			else if (pChoice.Hash === 'disconnect') { this._disconnectBoard(tmpID); }
			else if (pChoice.Hash === 'delete') { this._deleteBoard(tmpID); }
		});
	}

	// Rename the board: a small text-input dialog seeded with the current name; Enter or Save commits.
	_renameBoard(pID)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		let tmpRow = (this._ownerBoards || []).find((pB) => Number(pB.IDMoodboard) === Number(pID));
		let tmpCurrent = (tmpRow && tmpRow.Name) || '';
		if (!tmpModal || typeof tmpModal.show !== 'function') { return; }
		let tmpInputEl = null;
		tmpModal.show(
		{
			title: 'Rename whiteboard',
			width: '420px',
			closeable: true,
			content: '<div style="display:flex;flex-direction:column;gap:6px;">' +
				'<label style="font-size:12px;font-weight:600;letter-spacing:0.04em;text-transform:uppercase;color:var(--theme-color-text-secondary,#5b6376);">Name</label>' +
				'<input type="text" id="ps-board-rename-input" maxlength="200" value="' + _escapeHTML(tmpCurrent) + '" ' +
				'style="padding:8px 10px;border:1px solid var(--theme-color-border-default,#d8dde6);border-radius:6px;font-size:14px;' +
				'background:var(--theme-color-background-panel,#fff);color:var(--theme-color-text-primary,#2c3140);">' +
				'</div>',
			buttons:
			[
				{ Hash: 'cancel', Label: 'Cancel' },
				{ Hash: 'save', Label: 'Save', Style: 'primary' }
			],
			onOpen: (pDialog) =>
			{
				tmpInputEl = pDialog.querySelector('#ps-board-rename-input');
				if (!tmpInputEl) { return; }
				tmpInputEl.focus();
				tmpInputEl.select();
				// Enter commits by clicking the Save button (keeps the single dismiss path).
				tmpInputEl.addEventListener('keydown', (pKeyEvent) =>
				{
					if (pKeyEvent.key === 'Enter')
					{
						pKeyEvent.preventDefault();
						let tmpSaveBtn = pDialog.querySelector('[data-hash="save"]');
						if (tmpSaveBtn) { tmpSaveBtn.click(); }
					}
				});
			}
		}).then((pHash) =>
		{
			if (pHash !== 'save') { return; }
			let tmpName = (tmpInputEl && tmpInputEl.value != null) ? tmpInputEl.value.trim() : '';
			if (!tmpName) { this._toast('A whiteboard needs a name.', 'error'); return; }
			if (tmpName === tmpCurrent) { return; }
			let tmpAPI = this.pict.providers.MoodboardAPI;
			if (!tmpAPI) { return; }
			tmpAPI.update(pID, { Name: tmpName })
				.then(() =>
				{
					// Patch the cached row + repaint pills and the editor title (no re-fetch of board blobs).
					let tmpPatch = (this._ownerBoards || []).find((pB) => Number(pB.IDMoodboard) === Number(pID));
					if (tmpPatch) { tmpPatch.Name = tmpName; }
					this._rebuild();
					this._toast('Whiteboard renamed.', 'success');
				})
				.catch((pErr) => this._toast('Rename failed: ' + ((pErr && pErr.message) || pErr), 'error'));
		});
	}

	// Disconnect the board from this entity: clear its owner (and the legacy IDVision) so it becomes a
	// standalone whiteboard, still reachable from the Whiteboards page. The row is not deleted.
	_disconnectBoard(pID)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		let tmpDo = () =>
		{
			let tmpAPI = this.pict.providers.MoodboardAPI;
			if (!tmpAPI) { return; }
			tmpAPI.update(pID, { OwnerType: '', IDOwner: 0, IDVision: 0 })
				.then(() =>
				{
					this._dropBoardFromList(pID);
					this._toast('Whiteboard disconnected. It is now a standalone board on the Whiteboards page.', 'success');
				})
				.catch((pErr) => this._toast('Disconnect failed: ' + ((pErr && pErr.message) || pErr), 'error'));
		};
		let tmpMsg = 'Disconnect this whiteboard? It stays as a standalone whiteboard you can reach from the Whiteboards page, but it will no longer show here.';
		if (tmpModal && typeof tmpModal.confirm === 'function') { tmpModal.confirm(tmpMsg, { title: 'Disconnect whiteboard', confirmLabel: 'Disconnect' }).then((pOk) => { if (pOk) { tmpDo(); } }); }
		else { tmpDo(); }
	}

	// Delete the board outright (soft-delete via the Moodboard DELETE endpoint).
	_deleteBoard(pID)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		let tmpDo = () =>
		{
			let tmpAPI = this.pict.providers.MoodboardAPI;
			if (!tmpAPI) { return; }
			tmpAPI.remove(pID)
				.then(() =>
				{
					this._dropBoardFromList(pID);
					this._toast('Whiteboard deleted.', 'success');
				})
				.catch((pErr) => this._toast('Delete failed: ' + ((pErr && pErr.message) || pErr), 'error'));
		};
		if (tmpModal && typeof tmpModal.confirm === 'function') { tmpModal.confirm('Delete this whiteboard for good? This cannot be undone.', { title: 'Delete whiteboard', dangerous: true, confirmLabel: 'Delete' }).then((pOk) => { if (pOk) { tmpDo(); } }); }
		else { tmpDo(); }
	}

	// Drop a board out of this entity's cached list, pick a new selection (the first remaining, or none),
	// and repaint. Shared by disconnect and delete -- both leave the board out of this entity's picker.
	_dropBoardFromList(pID)
	{
		if (this._BoardSaveTimer) { clearTimeout(this._BoardSaveTimer); this._BoardSaveTimer = null; }
		this._ownerBoards = (this._ownerBoards || []).filter((pB) => Number(pB.IDMoodboard) !== Number(pID));
		if (Number(this._SelectedBoardID) === Number(pID))
		{
			this._SelectedBoardID = (this._ownerBoards[0] && this._ownerBoards[0].IDMoodboard) || 0;
		}
		this._VisionBoardEditing = false;
		this._VisionBoardLoadedFor = null;
		this._VisionBoardID = Number(this._SelectedBoardID) || 0;
		this._rebuild();
	}

	// Whether a loaded board has no content (no cards). Delegates to the moodboard module's own accessor
	// (isBoardEmpty) so the host does not re-encode the board's internal shape; if the view is not
	// registered yet it falls back to a Nodes check. Callers use this to collapse an empty board's
	// container rather than mount an empty box.
	_boardIsEmpty(pBoard)
	{
		let tmpView = this.pict.views['Vision-Board-View'] || this.pict.views['Vision-Board-Edit'];
		if (tmpView && typeof tmpView.isBoardEmpty === 'function') { return tmpView.isBoardEmpty(pBoard); }
		let tmpNodes = (pBoard && Array.isArray(pBoard.Nodes)) ? pBoard.Nodes : [];
		return tmpNodes.length === 0;
	}

	// True when none of these board rows carry content. A read-only viewer with only empty boards gets no
	// board section at all (rather than a header over a blank box); an editor always keeps it so they can
	// start one, and a board with content anywhere keeps it so a viewer can open that one.
	_allBoardsEmpty(pRows)
	{
		let tmpRows = Array.isArray(pRows) ? pRows : [];
		if (!tmpRows.length) { return true; }
		let tmpAPI = this.pict.providers.MoodboardAPI;
		if (!tmpAPI) { return false; }
		for (let i = 0; i < tmpRows.length; i++)
		{
			if (!this._boardIsEmpty(tmpAPI.loadBoard(tmpRows[i]))) { return false; }
		}
		return true;
	}

	// Mount the read-only (display-style) or the editable instance into #Vision-Board. The display-style
	// toggle + set-view-area live in the moodboard's own toolbar while editing; the host only sizes the
	// container when the style changes (onBoardDisplayStyleChanged).
	_renderVisionBoardView(pEditing, pBoard)
	{
		let tmpHost = document.getElementById('Vision-Board');
		if (tmpHost) { tmpHost.innerHTML = ''; }
		// An empty board mounted read-only is just an empty bordered box. Collapse the container instead of
		// mounting -- edit mode always mounts so a user can start adding content. Toggling Done on a board a
		// user left empty routes back here read-only and re-collapses; adding a node makes it mount.
		if (!pEditing && this._boardIsEmpty(pBoard))
		{
			if (tmpHost)
			{
				tmpHost.classList.add('ps-board-empty-collapsed');
				// Drop any presentation chrome a previous non-empty board left on the container / detail.
				tmpHost.classList.remove('ps-board-jumbotron', 'ps-board-background');
				let tmpDetail = (typeof tmpHost.closest === 'function') ? tmpHost.closest('.ps-detail') : null;
				if (tmpDetail) { tmpDetail.classList.remove('ps-detail-backdrop'); tmpDetail.style.paddingTop = ''; }
			}
			this._updateVisionEditButton();
			return;
		}
		if (tmpHost) { tmpHost.classList.remove('ps-board-empty-collapsed'); }
		this._VisionBoardViewName = pEditing ? 'Vision-Board-Edit' : 'Vision-Board-View';
		let tmpView = this.pict.views[this._VisionBoardViewName];
		if (!tmpView) { return; }
		tmpView.render();
		// Apply the vision's Drafting-Kit default connection style so newly drawn links match the craft.
		if (this._connectionStyle && typeof tmpView.setConnectionDefaults === 'function') { tmpView.setConnectionDefaults(this._connectionStyle); }
		// Apply the vision's Drafting-Kit sticker set so the board's gallery opens on the craft's stickers.
		if (this._stickerSet || this._stickerSetItems)
		{
			let tmpApp = this.pict.PictApplication;
			if (tmpApp && tmpApp._StickerSources && tmpApp._StickerSources.vision && typeof tmpApp._StickerSources.vision.setActiveSet === 'function')
			{
				// A custom set ships its resolved items from the lexicon (per-tenant rows not in the bundle);
				// a system set is just a key the source resolves from the in-bundle registry.
				tmpApp._StickerSources.vision.setActiveSet(this._stickerSetItems && this._stickerSetItems.length ? this._stickerSetItems : this._stickerSet);
			}
		}
		tmpView.setBoard(pBoard || { Nodes: [], Connections: [], ViewState: { PanX: 0, PanY: 0, Zoom: 1 } });
		// A board created via "+ Photo" drops its image in once the editor has mounted.
		if (pEditing && this._PendingPhoto && typeof tmpView.addImageFiles === 'function')
		{
			let tmpFile = this._PendingPhoto;
			this._PendingPhoto = null;
			setTimeout(() =>
			{
				try
				{
					// Full-bleed: a jumbotron fits the width of a frame seeded from the photo.
					if (typeof tmpView.setDisplayStyle === 'function') { tmpView.setDisplayStyle('jumbotron'); }
					tmpView.addImageFiles([tmpFile]);
				}
				catch (pErr) { /* leave the board empty */ }
			}, 200);
		}
		this._updateVisionEditButton();
	}

	// "Edit board" / "Done": flip the inspiration board between its saved display style (read-only) and the
	// editable canvas, in place. Editors only.
	toggleVisionBoardEdit()
	{
		let tmpApp = this.pict.PictApplication;
		if (!tmpApp || typeof tmpApp._can !== 'function' || !tmpApp._can('content.edit')) { return; }
		if (this._VisionBoardEditing)
		{
			// Exit: capture the edited board + persist, then back to the read-only display.
			let tmpEdit = this.pict.views['Vision-Board-Edit'];
			let tmpBoard = (tmpEdit && typeof tmpEdit.getBoard === 'function') ? tmpEdit.getBoard() : this._VisionBoard;
			this._VisionBoard = tmpBoard;
			let tmpAPI = this.pict.providers.MoodboardAPI;
			if (tmpAPI && this._VisionBoardID) { tmpAPI.saveBoard(this._VisionBoardID, tmpBoard).catch(() => {}); }
			this._VisionBoardEditing = false;
		}
		else
		{
			this._VisionBoardEditing = true;
		}
		this._renderVisionBoardView(this._VisionBoardEditing, this._VisionBoard);
	}

	_updateVisionEditButton()
	{
		let tmpButton = document.getElementById('Vision-Board-Edit-Toggle');
		if (tmpButton)
		{
			tmpButton.textContent = this._VisionBoardEditing ? 'Done' : 'Edit board';
			// Float the Done button over the fullscreen overlay while editing (see the CSS note above).
			tmpButton.classList.toggle('is-board-editing', !!this._VisionBoardEditing);
		}
	}

	// Lay out #Vision-Board for the current presentation, reported by the moodboard: a jumbotron is a hero
	// band (its scaled view-area frame height) above the detail; a background becomes a full-page layer
	// BEHIND the whole vision detail (the sections render on top); the canvas is the bounded box.
	onBoardDisplayStyleChanged(pInfo)
	{
		let tmpEl = document.getElementById('Vision-Board');
		if (!tmpEl || !pInfo) { return; }
		let tmpDetail = (typeof tmpEl.closest === 'function') ? tmpEl.closest('.ps-detail') : null;
		tmpEl.classList.remove('ps-board-jumbotron', 'ps-board-background');
		if (tmpDetail) { tmpDetail.classList.remove('ps-detail-backdrop'); tmpDetail.style.paddingTop = ''; }
		if (pInfo.effectiveStyle === 'jumbotron')
		{
			tmpEl.classList.add('ps-board-jumbotron');
			let tmpHeight = pInfo.jumbotronHeight;
			tmpEl.style.height = (tmpHeight > 0 ? Math.max(140, Math.min(560, tmpHeight)) : 320) + 'px';
		}
		else if (pInfo.effectiveStyle === 'background')
		{
			// Backdrop: the board fills the whole detail as a full-page layer behind the content
			// (ps-detail-backdrop CSS, z-index -1). The gear "Backdrop top margin" pushes the detail content
			// down so the top band shows the board art (24px is the base ps-detail padding).
			tmpEl.classList.add('ps-board-background');
			tmpEl.style.height = '';
			if (tmpDetail)
			{
				tmpDetail.classList.add('ps-detail-backdrop');
				let tmpMargin = (pInfo.topMargin > 0) ? pInfo.topMargin : 0;
				if (tmpMargin) { tmpDetail.style.paddingTop = (24 + tmpMargin) + 'px'; }
			}
		}
		else
		{
			tmpEl.style.height = '';
		}
	}

	// Autosave hook wired to Vision-Board-Edit. Keep the cached board fresh so a re-mount uses the latest
	// edits, and debounce the write.
	onBoardSaved(pBoard)
	{
		if (!this._VisionBoardID) { return; }
		this._VisionBoard = pBoard;
		// Keep the cached list row fresh so switching whiteboards and back shows the latest edits.
		let tmpRow = (this._ownerBoards || []).find((pB) => Number(pB.IDMoodboard) === Number(this._VisionBoardID));
		if (tmpRow) { try { tmpRow.BoardJSON = JSON.stringify(pBoard); } catch (pErr) { /* keep cache as-is */ } }
		if (this._BoardSaveTimer) { clearTimeout(this._BoardSaveTimer); }
		let tmpSelf = this;
		this._BoardSaveTimer = setTimeout(function ()
		{
			tmpSelf._BoardSaveTimer = null;
			let tmpAPI = tmpSelf.pict.providers.MoodboardAPI;
			if (tmpAPI) { tmpAPI.saveBoard(tmpSelf._VisionBoardID, pBoard).catch(() => {}); }
		}, 800);
	}

	// ── embedded comments section (Vision, WorkItem) ──────────────────────────

	// Mount (or re-mount) the comments section after a render. The detail re-renders on relation edits,
	// which wipes the #Entity-Comments container, so this runs every render: same record just repaints
	// the already-loaded threads; a new record re-points the section at the new context (it loads its
	// own data through PlansheetCommentProvider). Posting is gated to content.edit; others see read-only.
	_mountPrompts()
	{
		let tmpState = this.pict.AppData.EntityDetail;
		let tmpSlot = (tmpState && tmpState.PromptsSlot && tmpState.PromptsSlot[0]) ? tmpState.PromptsSlot[0] : null;
		if (!tmpSlot) { return; }
		let tmpView = this.pict.views['Plansheet-PromptAttachments'];
		if (!tmpView || typeof document === 'undefined') { return; }
		// Pass the human number (WorkItemNumber) so the panel can render the copy-paste MCP run
		// block, which names the work item by its number, not its raw id.
		let tmpDesc = libDescriptors.Entities[this._key] || {};
		let tmpRecord = this._loadedRecord || (this._provider(this._key) && this._provider(this._key).store().Index[this._id]) || null;
		let tmpNumber = (tmpRecord && tmpDesc.NumberField) ? (Number(tmpRecord[tmpDesc.NumberField]) || 0) : 0;
		tmpView.showFor(this._key, this._id, 'Entity-Prompts', tmpNumber);
	}

	// Mount read-only PromptEmbed cards for the prompts of this record's linked work items (descriptor
	// WorkItemPrompts: true -- Feedback). Gathers the FeedbackWorkItem join -> each work item's prompt
	// links, groups by work item, and mounts an embed per prompt with its MCP run command (when the plan
	// sheet has a Code). Read-only here: editing a prompt stays on the work item.
	_mountWorkItemPrompts()
	{
		let tmpState = this.pict.AppData.EntityDetail;
		let tmpSlot = (tmpState && tmpState.WorkItemPromptsSlot && tmpState.WorkItemPromptsSlot[0]) ? tmpState.WorkItemPromptsSlot[0] : null;
		if (!tmpSlot || typeof document === 'undefined') { return; }
		let tmpHost = document.getElementById('Entity-WorkItemPrompts');
		if (!tmpHost) { return; }
		let tmpJoin = libDescriptors.Joins.FeedbackWorkItem;
		let tmpCode = ((this.pict.AppData.Auth && this.pict.AppData.Auth.User && this.pict.AppData.Auth.User.CustomerCode) || '');
		this._wiPromptEmbeds = [];
		this.pict.providers.LinkAPI.listLinks(tmpJoin.Plural, 'IDFeedback', this._id)
			.then((pRows) =>
			{
				let tmpSeen = {};
				let tmpIDs = (pRows || []).map((pR) => Number(pR.IDWorkItem)).filter((pX) => (pX > 0 && !tmpSeen[pX] && (tmpSeen[pX] = true)));
				if (!tmpIDs.length) { return null; }
				return Promise.all(tmpIDs.map((pIDWI) => Promise.all(
				[
					libFrameworkClient.getJSON(this.pict, '/1.0/WorkItem/' + pIDWI).catch(() => null),
					this.pict.providers.PromptAPI.linksFor('WorkItem', pIDWI).catch(() => [])
				]).then((pPair) =>
				{
					let tmpWI = pPair[0]; if (Array.isArray(tmpWI)) { tmpWI = tmpWI[0]; }
					return { WI: tmpWI || { IDWorkItem: pIDWI }, Prompts: pPair[1] || [] };
				})));
			})
			.then((pGroups) =>
			{
				if (!pGroups) { tmpHost.innerHTML = '<div style="font-size:12.5px;color:var(--theme-color-text-secondary,#5b6376);">No linked work items yet.</div>'; return; }
				let tmpHTML = '';
				let tmpMounts = [];
				pGroups.forEach((pGroup, pGroupIdx) =>
				{
					if (!pGroup.Prompts.length) { return; }
					let tmpWI = pGroup.WI || {};
					let tmpNumber = tmpWI.WorkItemNumber || tmpWI.IDWorkItem;
					tmpHTML += '<div style="margin:8px 0 4px;font-size:11px;text-transform:uppercase;letter-spacing:0.04em;color:var(--theme-color-text-secondary,#5b6376);">Work Item #' + _escapeHTML(tmpNumber) + ' - ' + _escapeHTML(tmpWI.Title || '') + '</div>';
					pGroup.Prompts.forEach((pEntry, pPromptIdx) =>
					{
						let tmpHostID = 'wip-' + pGroupIdx + '-' + pPromptIdx;
						tmpHTML += '<div id="' + tmpHostID + '"></div>';
						let tmpPromptNum = (pEntry.Prompt && pEntry.Prompt.PromptNumber) || 0;
						let tmpRun = (tmpCode && tmpNumber && tmpPromptNum) ? ('Using the plansheet MCP, load the ' + tmpCode + ' plan sheet and run prompt ' + tmpPromptNum + ' for Work Item ' + tmpNumber + '.') : '';
						tmpMounts.push({ HostID: tmpHostID, Entry: pEntry, RunCommand: tmpRun });
					});
				});
				if (!tmpHTML) { tmpHTML = '<div style="font-size:12.5px;color:var(--theme-color-text-secondary,#5b6376);">The linked work items have no prompts yet.</div>'; }
				tmpHost.innerHTML = tmpHTML;
				tmpMounts.forEach((pMount) =>
				{
					let tmpEl = document.getElementById(pMount.HostID);
					if (!tmpEl) { return; }
					let tmpEmbed = new libPromptEmbed(this.pict);
					tmpEmbed.configure(pMount.Entry, { RunCommand: pMount.RunCommand, Editable: false });
					tmpEmbed.mount(tmpEl);
					this._wiPromptEmbeds.push(tmpEmbed);
				});
			})
			.catch(() => { /* a transient fetch failure just leaves the section empty */ });
	}

	_mountComments()
	{
		let tmpState = this.pict.AppData.EntityDetail;
		let tmpSlot = (tmpState && tmpState.CommentsSlot && tmpState.CommentsSlot[0]) ? tmpState.CommentsSlot[0] : null;
		if (!tmpSlot) { return; }
		let tmpView = this.pict.views['Plansheet-Comments'];
		if (!tmpView || typeof document === 'undefined') { return; }

		// Load the team roster once so the section can show author names for teammates.
		if (!this._CommentsRosterLoaded && this.pict.providers.TeamAPI && typeof this.pict.providers.TeamAPI.roster === 'function')
		{
			this._CommentsRosterLoaded = true;
			this.pict.providers.TeamAPI.roster().catch(() => {});
		}

		let tmpUser = (this.pict.AppData.Auth && this.pict.AppData.Auth.User) || {};
		tmpView.options.CurrentUser = { Key: tmpUser.IDUser || 0, Name: tmpUser.DisplayName || tmpUser.Email || 'You', Avatar: tmpUser.AvatarURL || '' };
		tmpView.options.ReadOnly = !tmpSlot.Editable;

		let tmpHost = document.getElementById('Entity-Comments');
		if (tmpHost) { tmpHost.innerHTML = ''; }

		if (this._CommentsLoadedFor === this._id)
		{
			tmpView.render();
			return;
		}
		this._CommentsLoadedFor = this._id;
		tmpView.setContext({ OwnerType: this._key, IDOwner: this._id });
	}

	// Mount the SHARED checklists for this entity (WorkItem) into #Entity-Checklists. The detail
	// re-render wipes the container, so re-point + repaint the embedded collection every render.
	_mountChecklists()
	{
		let tmpState = this.pict.AppData.EntityDetail;
		let tmpSlot = (tmpState && tmpState.ChecklistsSlot && tmpState.ChecklistsSlot[0]) ? tmpState.ChecklistsSlot[0] : null;
		if (!tmpSlot || typeof document === 'undefined') { return; }
		let tmpView = this.pict.views['Plansheet-Checklists-Embed'];
		if (!tmpView) { return; }
		if (!document.getElementById('Entity-Checklists')) { return; }
		tmpView.configure('owner', this._key, this._id);
	}

	// Mount the three refinement loops for a vision into #Entity-VisionLoops. Re-point + repaint every
	// render (the detail re-render wipes the container).
	_mountVisionLoops()
	{
		let tmpState = this.pict.AppData.EntityDetail;
		let tmpSlot = (tmpState && tmpState.LoopsSlot && tmpState.LoopsSlot[0]) ? tmpState.LoopsSlot[0] : null;
		if (!tmpSlot || typeof document === 'undefined') { return; }
		if (!document.getElementById('Entity-VisionLoops')) { return; }
		let tmpView = this.pict.views['Plansheet-VisionLoops'];
		if (tmpView && typeof tmpView.configure === 'function') { tmpView.configure(this._id); }
	}

	// Mount the connections grid for an entity into #Entity-Connections. Re-point + repaint every render.
	_mountConnections()
	{
		let tmpState = this.pict.AppData.EntityDetail;
		let tmpSlot = (tmpState && tmpState.ConnectionsSlot && tmpState.ConnectionsSlot[0]) ? tmpState.ConnectionsSlot[0] : null;
		if (!tmpSlot || typeof document === 'undefined') { return; }
		if (!document.getElementById('Entity-Connections')) { return; }
		let tmpView = this.pict.views['Plansheet-Connections'];
		let tmpDesc = libDescriptors.Entities[this._key] || {};
		if (tmpView && typeof tmpView.configure === 'function') { tmpView.configure(this._key, this._id, 'graphite', !!tmpDesc.Prompts); }
	}

	// The Content provider (pict-section-content) backs both the inline reads below and the
	// editor's live preview (via pict-section-markdowneditor), so a core doc and its editor
	// render identically -- same markdown parse, same Mermaid/KaTeX activation.
	_contentProvider() { return this.pict.providers.Content; }

	_renderCoreDoc()
	{
		let tmpBody = String(this._coreDocBody || '');
		if (!tmpBody.trim() || typeof document === 'undefined') { return; }
		let tmpContainer = document.getElementById('EntityDetail-CoreDoc');
		libMarkdownRender.renderInto(this._contentProvider(), tmpContainer, tmpBody);
	}

	// Render Markdown-flagged field bodies (built in _rebuild) into their per-field
	// containers, the same way the core document renders.
	_renderMarkdownFields()
	{
		if (typeof document === 'undefined') { return; }
		let tmpFields = (this.pict.AppData.EntityDetail && this.pict.AppData.EntityDetail.Fields) || [];
		for (let i = 0; i < tmpFields.length; i++)
		{
			let tmpSlot = (tmpFields[i].MDSlot && tmpFields[i].MDSlot[0]) || null;
			if (!tmpSlot) { continue; }
			let tmpContainer = document.getElementById(tmpSlot.ContainerID);
			libMarkdownRender.renderInto(this._contentProvider(), tmpContainer, tmpSlot.Body);
		}
	}

	// Mount (or re-mount) the embedded step editor after a render. The detail re-renders
	// on relation edits, which wipes the #Routine-Steps container, so this runs every
	// render: the same record repaints from the editor's AppData cache; a new record
	// reloads its steps from the server.
	_mountStepEditor()
	{
		let tmpState = this.pict.AppData.EntityDetail;
		let tmpSlot = (tmpState && tmpState.StepsSlot && tmpState.StepsSlot[0]) ? tmpState.StepsSlot[0] : null;
		if (!tmpSlot) { return; }
		let tmpView = this.pict.views['Plansheet-RoutineSteps'];
		if (!tmpView || typeof document === 'undefined') { return; }
		if (this._StepsLoadedFor === this._id)
		{
			tmpView.render();
			return;
		}
		this._StepsLoadedFor = this._id;
		tmpView.show(this._id, tmpSlot.Editable);
	}
}

module.exports = PictViewEntityDetail;

module.exports.default_configuration =
{
	ViewIdentifier: 'Plansheet-EntityDetail',
	DefaultRenderable: 'EntityDetail-Display',
	AutoRender: false,
	CSS: /*css*/`
		/* Per-vision craft picker in the detail header. */
		.ps-detail-craft { display: flex; align-items: center; gap: 8px; margin: -4px 0 18px; font-size: 13px; color: var(--theme-color-text-secondary, #5b6376); }
		.ps-detail-craft-label { text-transform: uppercase; letter-spacing: 0.04em; font-size: 11px; font-weight: 600; }
		.ps-detail-craft-current { color: var(--theme-color-text-primary, #2c3140); }
		/* Blueprint title block (WorkItem detail) -- a branded drafting sheet. */
		.ps-detail-blueprint { margin: 4px 0 22px; }
		.bp-sheet { background: #0E3460; border-radius: 12px; padding: 10px; }
		.bp-frame { border: 1.4px solid #EAF2FC; border-radius: 6px; padding: 16px 18px; position: relative; }
		.bp-head { display: flex; align-items: baseline; gap: 12px; margin-bottom: 14px; }
		.bp-kicker { font-family: ui-monospace, Menlo, monospace; font-size: 11px; letter-spacing: 3px; color: #7FA8DA; }
		.bp-title { font-size: 16px; font-weight: 700; color: #EAF2FC; }
		.bp-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 10px; }
		.bp-cell { border: 1px solid rgba(234,242,252,0.4); border-radius: 5px; padding: 10px 12px; background: #123A64; min-height: 64px; }
		.bp-cell-head { display: flex; align-items: baseline; gap: 8px; }
		.bp-count { font-size: 26px; font-weight: 700; color: #EAF2FC; line-height: 1; }
		.bp-label { font-family: ui-monospace, Menlo, monospace; font-size: 11px; letter-spacing: 1px; color: #9DBDE6; text-transform: uppercase; }
		.bp-items { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 8px; }
		.bp-item { font-size: 11px; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; padding: 2px 7px; border-radius: 4px; background: rgba(234,242,252,0.10); color: #EAF2FC; cursor: pointer; }
		.bp-item:hover { background: rgba(234,242,252,0.20); }
		.bp-item.is-pinned { box-shadow: inset 0 0 0 1px #F2C84B; }
		.bp-item-x { border: none; background: none; color: #F2C84B; cursor: pointer; font-size: 13px; line-height: 1; padding: 0 0 0 5px; margin: 0; }
		.bp-item-x:hover { color: #fff; }
		/* Pin composer: one framed navy panel so the two pickers + button read as a single deliberate
		   control. The pict-section-picker's theme tokens are re-mapped to the blueprint palette (same
		   technique as the connections linker) so both pickers look sexy and in-theme, not like a
		   light-theme control dropped on the sheet. */
		.bp-add {
			margin-top: 16px; padding: 13px 15px 15px; border-radius: 8px;
			border: 1px solid rgba(234,242,252,0.4); background: #123A64;
			display: flex; flex-direction: column; gap: 11px;
			--theme-color-background-primary: rgba(8,18,36,0.55);
			--theme-color-background-panel: #0E3460;
			--theme-color-background-secondary: rgba(9,22,44,0.6);
			--theme-color-background-tertiary: rgba(150,180,220,0.22);
			--theme-color-background-hover: rgba(150,180,220,0.16);
			--theme-color-text-primary: #EAF2FC;
			--theme-color-text-secondary: #9DBDE6;
			--theme-color-text-muted: #7FA8DA;
			--theme-color-border-default: rgba(150,180,220,0.36);
			--theme-color-border-light: rgba(150,180,220,0.30);
			--theme-color-border-strong: rgba(234,242,252,0.5);
			--theme-color-brand-primary: #F2C84B;
		}
		.bp-add-kicker { font-family: ui-monospace, Menlo, monospace; font-size: 11px; letter-spacing: 2px; color: #7FA8DA; text-transform: uppercase; }
		.bp-add-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
		.bp-add-field > .pict-section-picker, .bp-add-field > div { width: 100%; }
		.bp-add-type { flex: 0 0 auto; width: 148px; }
		.bp-add-item { flex: 1 1 220px; min-width: 190px; }
		.bp-add-btn { flex: 0 0 auto; font: inherit; font-size: 13px; padding: 8px 18px; border-radius: 7px; border: 1.5px solid #F2C84B; background: transparent; color: #F2C84B; cursor: pointer; }
		.bp-add-btn:hover { background: rgba(242,200,75,0.15); }
		.bp-empty { font-size: 12px; color: #9DBDE6; padding: 6px 2px; }
		.bp-titleblock { display: flex; gap: 0; margin-top: 14px; border: 1px solid rgba(234,242,252,0.5); border-radius: 4px; overflow: hidden; }
		.bp-tb-cell { display: flex; flex-direction: column; gap: 2px; padding: 6px 12px; border-right: 1px solid rgba(234,242,252,0.3); }
		.bp-tb-k { font-family: ui-monospace, Menlo, monospace; font-size: 8px; letter-spacing: 1px; color: #7FA8DA; }
		.bp-tb-v { font-family: ui-monospace, Menlo, monospace; font-size: 12px; color: #EAF2FC; }
		/* root container styling comes from .ps-page .ps-page-narrow (Shell) */
		.ps-detail-head { margin-bottom: 20px; }
		.ps-detail-back { background: none; border: none; padding: 0; margin: 0 0 10px; color: var(--theme-color-text-secondary, #5b6376); cursor: pointer; font-size: 13px; line-height: 1; }
		.ps-detail-back:hover { color: var(--theme-color-brand-primary, #2988df); }
		.ps-detail-title { font-size: 28px; font-weight: 700; color: var(--theme-color-text-primary, #2c3140); margin: 0 0 14px; line-height: 1.2; }
		/* "Addressed" pill next to a handled feedback's title. */
		.ps-addressed-badge { display: inline-block; vertical-align: middle; margin-left: 12px; padding: 3px 12px; border-radius: 999px;
			font-size: 13px; font-weight: 600; letter-spacing: 0.02em;
			background: var(--theme-color-status-success-subtle, #e7f4ec); color: var(--theme-color-status-success, #2e7d32);
			border: 1px solid var(--theme-color-status-success, #2e7d32); }
		.ps-detail-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
		.ps-detail-actions:empty { display: none; }
		.ps-detail-fields {
			display: flex; flex-wrap: wrap; align-items: stretch;
			background: var(--theme-color-background-panel, #fff); border: 1px solid var(--theme-color-border-default, #e6e9ee);
			border-radius: 10px; overflow: hidden; margin-bottom: 20px;
		}
		.ps-detail-fields:empty { display: none; }
		.ps-detail-fcell { flex: 1 1 0; min-width: 130px; padding: 10px 16px; border-right: 1px solid var(--theme-color-border-default, #e6e9ee); }
		/* The rightmost stat cell on a row: drop its divider so it never dangles into empty space -- when it
		   is the last cell overall, or the last stat cell before a full-width (is-wide) row begins. */
		.ps-detail-fcell:last-child:not(.is-wide) { border-right: none; }
		.ps-detail-fcell:not(.is-wide):has(+ .is-wide) { border-right: none; }
		.ps-detail-fcell.is-wide { flex: 1 1 100%; border-right: none; border-top: 1px solid var(--theme-color-border-default, #e6e9ee); }
		.ps-detail-fcell.is-wide:first-child { border-top: none; }
		.ps-detail-flabel { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.07em; color: var(--theme-color-text-secondary, #5b6376); }
		.ps-detail-fval { font-size: 14px; color: var(--theme-color-text-primary, #2c3140); margin-top: 4px; white-space: pre-wrap; }
		.ps-detail-board-wrap { margin-bottom: 20px; }
		.ps-detail-board-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin: 0 0 10px; flex-wrap: wrap; }
		.ps-detail-board-h { margin: 0; font-size: 13px; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-secondary, #5b6376); }
		.ps-detail-board { height: 320px; position: relative; border: 1px solid var(--theme-color-border-default, #e6e9ee); border-radius: 10px; overflow: hidden; background: var(--theme-color-background-secondary, #f4f6f9); }
		/* An empty board mounted read-only would be just an empty bordered box; the host adds this class to
		   collapse the container entirely (the section header + Edit affordance stay for editors). */
		.ps-detail-board.ps-board-empty-collapsed { display: none; }
		/* A jumbotron board reads as a hero band (its height is set to the scaled view-area frame), a
		   background board as a taller full-width backdrop. The host JS sets the inline height; these just
		   tune the chrome so the band / backdrop sits flush like a banner rather than a bordered widget. */
		.ps-detail-board.ps-board-jumbotron, .ps-detail-board.ps-board-background { border-color: transparent; background: var(--theme-color-background-panel, #fff); }
		/* Backdrop display style (view mode): the board becomes a full-page layer BEHIND the whole vision
		   detail; the sections render on top. isolation:isolate keeps the z-index:-1 board inside the detail's
		   own stacking context (not behind the page); pointer-events:none keeps it a non-interactive backdrop.
		   The host pushes the content down (padding-top) by the gear "Backdrop top margin". */
		.ps-detail.ps-detail-backdrop { position: relative; isolation: isolate; }
		.ps-detail-backdrop .ps-detail-board { position: absolute; inset: 0; height: auto !important; z-index: -1; border: none; border-radius: 0; pointer-events: none; }
		/* The "Edit board" / "Done" toggle next to the heading (editors only). The display-style toggle +
		   set-view-area live in the moodboard's own toolbar while editing. */
		.ps-board-edit-toggle { padding: 4px 12px; font-size: 12px; }
		/* The "manage this whiteboard" kebab next to the Edit toggle: opens rename / disconnect / delete
		   for the selected board (editors only). */
		.ps-board-menu-btn { padding: 4px 10px; font-size: 16px; line-height: 1; font-weight: 700; }
		/* While editing, the inspiration board auto-enters the flow's full-viewport fullscreen
		   (Vision-Board-Edit FullscreenOnEdit), whose position:fixed overlay covers the detail chrome.
		   Float the Done toggle above that overlay (z-index over the flow's 9999) so exiting edit stays one
		   click; it doubles as a visible fallback if fullscreen never engages. */
		.ps-board-edit-toggle.is-board-editing
		{
			position: fixed; top: 14px; right: 18px; z-index: 10001;
			padding: 8px 18px; font-size: 13px;
			background: var(--theme-color-brand-primary, #3b6fd4); color: #fff;
			border-color: var(--theme-color-brand-primary, #3b6fd4);
			box-shadow: 0 2px 12px rgba(20, 28, 45, 0.32);
		}
		/* Whiteboard picker pills (an owner can have several boards). */
		.ps-board-tabs { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; flex: 1; }
		.ps-board-tab { padding: 3px 11px; font-size: 12px; border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 14px; background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-secondary, #5b6376); cursor: pointer; }
		.ps-board-tab:hover { border-color: var(--theme-color-brand-primary, #3b6fd4); color: var(--theme-color-text-primary, #2c3140); }
		.ps-board-tab.is-active { background: var(--theme-color-brand-primary, #3b6fd4); border-color: var(--theme-color-brand-primary, #3b6fd4); color: #fff; }
		.ps-board-tab-new { border-style: dashed; }
		.ps-board-empty { padding: 14px; border: 1px dashed var(--theme-color-border-default, #d8dde6); border-radius: 10px; color: var(--theme-color-text-secondary, #5b6376); font-size: 13px; margin-bottom: 10px; }
		.ps-rel {
			background: var(--theme-color-background-panel, #fff); border: 1px solid var(--theme-color-border-default, #e6e9ee);
			border-radius: 10px; padding: 14px 16px; margin-bottom: 14px;
		}
		.ps-rel h4 { margin: 0 0 10px; font-size: 13px; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-secondary, #5b6376); }
		.ps-rel-items { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 10px; }
		.ps-rel-empty { font-size: 13px; color: var(--theme-color-text-secondary, #5b6376); font-style: italic; }
		.ps-chip {
			display: inline-flex; align-items: center; gap: 6px; padding: 4px 6px 4px 10px;
			background: var(--theme-color-background-secondary, #eef1f6); border-radius: 999px; font-size: 13px;
			color: var(--theme-color-text-primary, #2c3140);
		}
		.ps-chip a.ps-link { color: var(--theme-color-brand-primary, #2988df); cursor: pointer; text-decoration: none; }
		.ps-chip a.ps-link:hover { text-decoration: underline; }
		.ps-chip-rel { font-size: 11px; color: var(--theme-color-text-secondary, #5b6376); background: var(--theme-color-background-panel, #fff); padding: 0 6px; border-radius: 999px; }
		.ps-chip-sub { font-size: 11px; color: var(--theme-color-text-secondary, #5b6376); }
		.ps-chip-x { border: none; background: transparent; cursor: pointer; color: var(--theme-color-status-danger, #c0392b); font-size: 15px; line-height: 1; padding: 0 2px; }
		.ps-rel-add { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
		.ps-rel-target { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
		/* The relation record pickers' host divs (WI #446) -- a plain fk/target picker (no sibling in its
		   row) just wants the full row width; one sharing a flex row with a button (link-add, subvision-
		   add) needs to claim the remaining space instead of shrinking to its content, same sizing as the
		   Blueprint composer's .bp-add-item. */
		.ps-input-picker { width: 100%; }
		.ps-rel-picker { flex: 1 1 220px; min-width: 190px; }
		.ps-chip-title { font-weight: 500; }
		.ps-input-sm { padding: 2px 6px; font-size: 12px; }
		.ps-media-grid { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 10px; }
		.ps-media-item { position: relative; }
		.ps-media-thumb { width: 120px; height: 84px; object-fit: cover; border-radius: 8px; border: 1px solid var(--theme-color-border-default, #d8dde6); display: block; background: var(--theme-color-background-secondary, #f1f3f7); }
		.ps-media-file { display: inline-flex; align-items: center; gap: 8px; padding: 10px 14px; border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 8px; background: var(--theme-color-background-secondary, #f1f3f7); text-decoration: none; }
		.ps-media-file-type { font-size: 10px; text-transform: uppercase; letter-spacing: 0.04em; background: var(--theme-color-background-panel, #fff); padding: 1px 6px; border-radius: 999px; color: var(--theme-color-text-secondary, #5b6376); }
		.ps-media-x { position: absolute; top: -8px; right: -8px; background: var(--theme-color-background-panel, #fff); border-radius: 999px; width: 20px; height: 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.2); }
		.ps-file-btn { cursor: pointer; }
			.ps-detail-sub { margin: -8px 0 16px; color: var(--theme-color-text-secondary, #5b6376); font-size: 15px; }
		.ps-detail-sub:empty { display: none; }
		.ps-detail-notfound { margin: 8px 0 4px; padding: 16px 18px; border: 1px solid var(--theme-color-border-default, #e6e9ee); border-radius: 10px; background: var(--theme-color-background-secondary, #f4f6f9); color: var(--theme-color-text-secondary, #5b6376); font-size: 15px; }
			.ps-detail-sub:empty { display: none; }
			.ps-coredoc-body { background: var(--theme-color-background-panel, #fff); border: 1px solid var(--theme-color-border-default, #e6e9ee); border-radius: 8px; padding: 16px 18px; margin-bottom: 10px; max-height: 560px; overflow-y: auto; }
			.ps-coredoc-body img { max-width: 100%; border-radius: 6px; }
			.ps-coredoc-body pre { background: var(--theme-color-background-secondary, #f1f3f7); padding: 10px; border-radius: 6px; overflow-x: auto; }
			.ps-coredoc-empty { color: var(--theme-color-text-secondary, #5b6376); font-style: italic; margin-bottom: 10px; }
			.ps-subv-grid { display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 10px; }
			.ps-subv-card { width: 180px; padding: 10px 12px; border: 1px solid var(--theme-color-border-default, #e6e9ee); border-radius: 8px; background: var(--theme-color-background-panel, #fff); cursor: pointer; }
			.ps-subv-card:hover { border-color: var(--theme-color-brand-primary, #2988df); }
			.ps-subv-card-head { display: flex; align-items: center; justify-content: space-between; }
			.ps-subv-num { font-size: 11px; font-weight: 600; color: var(--theme-color-text-secondary, #5b6376); }
			.ps-subv-name { font-size: 14px; font-weight: 600; color: var(--theme-color-text-primary, #2c3140); margin: 4px 0 2px; }
			.ps-subv-sub { font-size: 11px; color: var(--theme-color-text-secondary, #5b6376); }
			.ps-subv-partof { font-size: 13px; color: var(--theme-color-text-secondary, #5b6376); margin-bottom: 10px; display: flex; align-items: center; flex-wrap: wrap; gap: 6px; }
			.ps-subv-partof-label { font-weight: 600; }
			.ps-detail-steps-wrap { margin-bottom: 20px; }
			.ps-detail-fmd { white-space: normal; }
			.ps-detail-fmd p:first-child { margin-top: 0; }
			.ps-detail-fmd p:last-child { margin-bottom: 0; }
			.ps-detail-fmd img { max-width: 100%; border-radius: 6px; }
			.ps-detail-fmd pre { background: var(--theme-color-background-secondary, #f1f3f7); padding: 10px; border-radius: 6px; overflow-x: auto; }
			.ps-media-video { width: 240px; max-height: 160px; border-radius: 8px; border: 1px solid var(--theme-color-border-default, #d8dde6); background: #000; display: block; }
			.ps-story { margin: 18px 0; }
			.ps-review-summary { font-size: 12px; font-weight: 600; color: var(--theme-color-text-secondary,#5b6376); }
			.ps-review-summary.ps-review-ok { color: #3fae6b; }
			.ps-review-summary.ps-review-warn { color: #d0555b; }
			.ps-review-dot { background: #d0555b; color: #fff; }
			.ps-review-reason { font-weight: 600; color: var(--theme-color-text-primary,#1f2733); }
			.ps-review-issues { background: rgba(208,85,91,0.12); color: #d0555b; }
			.ps-review-note { margin-top: 4px; font-size: 13px; color: var(--theme-color-text-secondary,#5b6376); white-space: pre-wrap; }
			.ps-story-head { display: flex; align-items: baseline; gap: 10px; margin-bottom: 12px; }
			.ps-story-h { font-size: 14px; font-weight: 700; color: var(--theme-color-text-primary, #2c3140); margin: 0; }
			.ps-story-count { font-size: 12px; color: var(--theme-color-text-secondary, #5b6376); }
			.ps-story-line { list-style: none; margin: 0; padding: 0; position: relative; }
			.ps-story-line::before { content: ''; position: absolute; left: 11px; top: 6px; bottom: 6px; width: 2px; background: var(--theme-color-border-default, #e6e9ee); }
			.ps-story-node { position: relative; display: flex; gap: 14px; padding-bottom: 14px; }
			.ps-story-node:last-child { padding-bottom: 0; }
			.ps-story-dot { flex: 0 0 auto; z-index: 1; width: 24px; height: 24px; border-radius: 999px; background: var(--theme-color-background-panel, #fff); border: 2px solid var(--theme-color-brand-primary, #2988df); color: var(--theme-color-brand-primary, #2988df); font-size: 12px; font-weight: 700; display: flex; align-items: center; justify-content: center; }
			.ps-story-card { flex: 1 1 auto; min-width: 0; background: var(--theme-color-background-panel, #fff); border: 1px solid var(--theme-color-border-default, #e6e9ee); border-radius: 8px; padding: 10px 14px; }
			.ps-story-card-head { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
			.ps-story-title { font-size: 14px; font-weight: 600; cursor: pointer; }
			.ps-story-kind { font-size: 9.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; padding: 1px 7px; border-radius: 999px; background: var(--theme-color-background-secondary, #f1f3f7); color: var(--theme-color-text-secondary, #5b6376); white-space: nowrap; }
			.ps-story-detail { font-size: 12px; color: var(--theme-color-text-secondary, #5b6376); }
			.ps-story-kind-feedback .ps-story-dot { border-color: #16a085; color: #16a085; }
			.ps-story-kind-prompt .ps-story-dot { border-color: #8e6fd8; color: #8e6fd8; }
			.ps-story-kind-run .ps-story-dot { border-color: #d9923c; color: #d9923c; }
			.ps-story-kind-validated .ps-story-dot { border-color: #27ae60; color: #27ae60; }
			.ps-story-kind-asbuilt .ps-story-dot { border-color: #2988df; color: #2988df; }
			.ps-story-meta { display: flex; align-items: center; flex-wrap: wrap; gap: 10px; margin-top: 3px; font-size: 12px; color: var(--theme-color-text-secondary, #5b6376); }
			.ps-story-model { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 11px; padding: 1px 6px; border-radius: 4px; background: var(--theme-color-background-secondary, #f1f3f7); }
			.ps-story-stats { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 7px; }
			.ps-story-stat { font-size: 11px; color: var(--theme-color-text-secondary, #5b6376); background: var(--theme-color-background-secondary, #f1f3f7); padding: 2px 8px; border-radius: 999px; }
			.ps-story-outcome { margin-left: auto; font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; padding: 2px 9px; border-radius: 999px; border: 1px solid var(--theme-color-border-default, #d8dde6); color: var(--theme-color-text-secondary, #5b6376); }
			.ps-story-outcome-delivered { border-color: var(--theme-color-status-success, #27ae60); color: var(--theme-color-status-success, #27ae60); }
			.ps-story-outcome-partial { border-color: var(--theme-color-brand-primary, #2988df); color: var(--theme-color-brand-primary, #2988df); }
			.ps-story-outcome-abandoned { border-color: var(--theme-color-status-danger, #c0392b); color: var(--theme-color-status-danger, #c0392b); }
			.ps-story-empty { color: var(--theme-color-text-secondary, #5b6376); font-style: italic; font-size: 13px; }
	` + libShareUI.SHARE_CSS,
	Templates:
	[
		{
			Hash: 'EntityDetail-Display',
			Template: /*html*/`
<div class="ps-page ps-page-narrow ps-detail">
	<div class="ps-detail-head">
		<button class="ps-detail-back" onclick="_Pict.views['Plansheet-EntityDetail'].back()">← {~D:AppData.EntityDetail.BackLabel~}</button>
		<h1 class="ps-detail-title">{~D:AppData.EntityDetail.HeaderTitle~}{~TS:Detail-AddressedBadge:AppData.EntityDetail.AddressedBadge~}</h1>
		<div class="ps-detail-actions">
			{~TS:Detail-AddressToggle:AppData.EntityDetail.AddressToggle~}
			{~TS:Detail-HeaderAction:AppData.EntityDetail.HeaderActions~}
			{~TS:Detail-Edit:AppData.EntityDetail.EditSlot~}
			{~TS:Detail-Delete:AppData.EntityDetail.DeleteSlot~}
		</div>
	</div>
	<div class="ps-detail-sub">{~D:AppData.EntityDetail.Subtitle~}</div>
	{~TS:Detail-NotFound:AppData.EntityDetail.NotFoundSlot~}
	{~TS:Detail-Craft:AppData.EntityDetail.CraftSlot~}
	{~TS:Detail-Blueprint:AppData.EntityDetail.BlueprintSlot~}
	{~TS:Detail-Board:AppData.EntityDetail.BoardSlot~}
	{~TS:Detail-VisionLoops:AppData.EntityDetail.LoopsSlot~}
	<div class="ps-detail-fields">{~TS:Detail-Field:AppData.EntityDetail.Fields~}</div>
	{~TS:Detail-CoreDoc:AppData.EntityDetail.CoreDocSlot~}
	{~TS:Detail-Steps:AppData.EntityDetail.StepsSlot~}
	{~TS:Detail-Connections:AppData.EntityDetail.ConnectionsSlot~}
	{~TS:Detail-WorkItemPrompts:AppData.EntityDetail.WorkItemPromptsSlot~}
	{~TS:Detail-Story:AppData.EntityDetail.StorySlot~}
	{~TS:Detail-ReviewHistory:AppData.EntityDetail.ReviewHistorySlot~}
	{~TS:Detail-Section:AppData.EntityDetail.Sections~}
	{~TS:Detail-Prompts:AppData.EntityDetail.PromptsSlot~}
	{~TS:Detail-Checklists:AppData.EntityDetail.ChecklistsSlot~}
	{~TS:Detail-Comments:AppData.EntityDetail.CommentsSlot~}
</div>`
		},
		// The header Edit button, as a slot so the not-found state can drop it (an empty .ps-detail-actions
		// then collapses via CSS, hiding the whole action bar). See WI #329.
		{ Hash: 'Detail-Edit', Template: /*html*/`<button class="ps-btn" onclick="_Pict.views['Plansheet-EntityDetail'].edit()">Edit</button>` },

		// The not-found message, shown in place of the field table when the record is not in this tenant.
		{ Hash: 'Detail-NotFound', Template: /*html*/`<div class="ps-detail-notfound">{~D:Record.Message~}</div>` },
		{
			Hash: 'Detail-Board',
			Template: /*html*/`
<div class="ps-detail-board-wrap">
	<div class="ps-detail-board-head">
		<h4 class="ps-detail-board-h">{~D:Record.Label~}</h4>
		<div class="ps-board-tabs">{~TS:Detail-Board-Pill:Record.Boards~}{~TS:Detail-Board-New:Record.AddSlot~}{~TS:Detail-Board-Photo:Record.AddSlot~}{~TS:Detail-Board-Attach:Record.AddSlot~}</div>
		{~TS:Detail-Board-EditToggle:Record.EditSlot~}
		{~TS:Detail-Board-Menu:Record.MenuSlot~}
	</div>
	{~TS:Detail-Board-Empty:Record.EmptySlot~}
	<input type="file" id="Entity-Board-PhotoInput" accept="image/*" style="display:none;" onchange="_Pict.views['Plansheet-EntityDetail'].onBoardPhotoPicked(this)">
	<div class="ps-detail-board" id="{~D:Record.ContainerID~}"></div>
</div>`
		},
		{ Hash: 'Detail-Board-Pill', Template: /*html*/`<button class="ps-board-tab {~D:Record.ActiveClass~}" onclick="_Pict.views['Plansheet-EntityDetail'].selectBoard({~D:Record.ID~})">{~D:Record.Name~}</button>` },
		{ Hash: 'Detail-Board-New', Template: /*html*/`<button class="ps-board-tab ps-board-tab-new" onclick="_Pict.views['Plansheet-EntityDetail'].newBoard()">+ New</button>` },
		{ Hash: 'Detail-Board-Photo', Template: /*html*/`<button class="ps-board-tab ps-board-tab-new" onclick="_Pict.views['Plansheet-EntityDetail'].pickBoardPhoto()">+ Photo</button>` },
		{ Hash: 'Detail-Board-Attach', Template: /*html*/`<button class="ps-board-tab ps-board-tab-new" title="Attach an existing whiteboard to this item" onclick="_Pict.views['Plansheet-EntityDetail'].attachExistingBoard(this)">+ Attach</button>` },
		{ Hash: 'Detail-Board-Empty', Template: /*html*/`<div class="ps-board-empty">No whiteboards here yet.</div>` },
		{
			Hash: 'Detail-Board-EditToggle',
			Template: /*html*/`<button class="ps-btn ps-board-edit-toggle" id="Vision-Board-Edit-Toggle" onclick="_Pict.views['Plansheet-EntityDetail'].toggleVisionBoardEdit()">Edit board</button>`
		},
		{
			Hash: 'Detail-Board-Menu',
			Template: /*html*/`<button class="ps-btn ps-board-menu-btn" id="Vision-Board-Menu-Btn" title="Manage this whiteboard (rename, disconnect, delete)" onclick="_Pict.views['Plansheet-EntityDetail'].boardMenu(this)">&#8942;</button>`
		},
		{
			Hash: 'Detail-Comments',
			Template: /*html*/`
<div class="ps-detail-comments-wrap">
	<h4 class="ps-detail-comments-h">{~D:Record.Label~}</h4>
	<div class="ps-detail-comments" id="{~D:Record.ContainerID~}"></div>
</div>`
		},
		{
			Hash: 'Detail-Checklists',
			Template: /*html*/`<div class="ps-detail-checklists-wrap"><div id="{~D:Record.ContainerID~}"></div></div>`
		},
		{
			Hash: 'Detail-VisionLoops',
			Template: /*html*/`<div class="ps-detail-loops-wrap" style="margin:18px 0;"><h4 style="font-size:14px;font-weight:700;color:var(--theme-color-text-primary,#2c3140);margin:0 0 10px;">{~D:Record.Label~}</h4><div id="{~D:Record.ContainerID~}"></div></div>`
		},
		{
			Hash: 'Detail-Blueprint',
			Template: /*html*/`<div class="ps-detail-blueprint" id="{~D:Record.ContainerID~}"></div>`
		},
		{
			Hash: 'Detail-Connections',
			Template: /*html*/`<div class="ps-detail-connections" id="{~D:Record.ContainerID~}" style="margin:18px 0;"></div>`
		},
		{
			Hash: 'Detail-Story',
			Template: /*html*/`
<div class="ps-story">
	<div class="ps-story-head">
		<h4 class="ps-story-h">{~D:Record.Label~}</h4>
		<span class="ps-story-count">{~D:Record.CountLabel~}</span>
	</div>
	<ol class="ps-story-line">{~TS:Detail-Story-Item:Record.Items~}</ol>
	{~TS:Detail-Story-Empty:Record.EmptySlot~}
</div>`
		},
		{
			Hash: 'Detail-Story-Item',
			Template: /*html*/`
<li class="ps-story-node {~D:Record.KindClass~}">
	<span class="ps-story-dot">{~D:Record.Num~}</span>
	<div class="ps-story-card">
		<div class="ps-story-card-head">
			<span class="ps-story-kind">{~D:Record.KindLabel~}</span>
			{~TS:Detail-Story-TitleLink:Record.LinkSlot~}{~TS:Detail-Story-TitlePlain:Record.PlainSlot~}
			<span class="ps-story-outcome {~D:Record.OutcomeClass~}">{~D:Record.OutcomeLabel~}</span>
		</div>
		<div class="ps-story-meta">
			<span class="ps-story-when">{~D:Record.When~}</span>
			{~TS:Detail-Story-Model:Record.ModelSlot~}
			{~TS:Detail-Story-Detail:Record.DetailSlot~}
		</div>
		<div class="ps-story-stats">{~TS:Detail-Story-Stat:Record.Stats~}</div>
	</div>
</li>`
		},
		{ Hash: 'Detail-Story-TitleLink', Template: /*html*/`<a class="ps-link ps-story-title" onclick="_Pict.views['Plansheet-EntityDetail'].openSession({~D:Record.ID~})">{~D:Record.Title~}</a>` },
		{ Hash: 'Detail-Story-TitlePlain', Template: /*html*/`<span class="ps-story-title">{~D:Record.Title~}</span>` },
		{ Hash: 'Detail-Story-Detail', Template: /*html*/`<span class="ps-story-detail">{~D:Record.Detail~}</span>` },
		{ Hash: 'Detail-Story-Model', Template: /*html*/`<span class="ps-story-model">{~D:Record.Model~}</span>` },
		{ Hash: 'Detail-Story-Stat', Template: /*html*/`<span class="ps-story-stat">{~D:Record.Text~}</span>` },
		{ Hash: 'Detail-Story-Empty', Template: /*html*/`<div class="ps-story-empty">No activity yet. As feedback, prompts, runs, and sessions land on this work item, they show here.</div>` },
		{
			Hash: 'Detail-ReviewHistory',
			Template: /*html*/`
<div class="ps-story ps-review">
	<div class="ps-story-head">
		<h4 class="ps-story-h">{~D:Record.Label~}</h4>
		<span class="ps-review-summary {~D:Record.SummaryClass~}">{~D:Record.Summary~}</span>
	</div>
	<ol class="ps-story-line">{~TS:Detail-ReviewHistory-Item:Record.Items~}</ol>
	{~TS:Detail-ReviewHistory-Empty:Record.EmptySlot~}
</div>`
		},
		{
			Hash: 'Detail-ReviewHistory-Item',
			Template: /*html*/`
<li class="ps-story-node">
	<span class="ps-story-dot ps-review-dot">{~D:Record.Num~}</span>
	<div class="ps-story-card">
		<div class="ps-story-card-head">
			<span class="ps-review-reason">Did not pass QC &mdash; {~D:Record.Reason~}</span>
			{~TS:Detail-ReviewHistory-Issues:Record.IssuesSlot~}
		</div>
		<div class="ps-story-meta"><span class="ps-story-when">{~D:Record.When~}</span></div>
		{~TS:Detail-ReviewHistory-Note:Record.NoteSlot~}
	</div>
</li>`
		},
		{ Hash: 'Detail-ReviewHistory-Note', Template: /*html*/`<div class="ps-review-note">{~D:Record.Note~}</div>` },
		{ Hash: 'Detail-ReviewHistory-Issues', Template: /*html*/`<span class="ps-story-outcome ps-review-issues">{~D:Record.Label~}</span>` },
		{ Hash: 'Detail-ReviewHistory-Empty', Template: /*html*/`<div class="ps-story-empty">No QC failures. When a reviewer sends this back, the reason shows here.</div>` },
		{
			Hash: 'Detail-Craft',
			Template: /*html*/`<div class="ps-detail-craft"><span class="ps-detail-craft-label">Craft</span>{~TS:Detail-Craft-Edit:Record.EditSlot~}{~TS:Detail-Craft-Read:Record.ReadSlot~}</div>`
		},
		{ Hash: 'Detail-Craft-Edit', Template: /*html*/`<select class="ps-input ps-input-sm" onchange="_Pict.views['Plansheet-EntityDetail'].setCraft(this.value)">{~TS:Detail-CraftOpt:Record.Options~}</select>` },
		{ Hash: 'Detail-Craft-Read', Template: /*html*/`<span class="ps-detail-craft-current">{~D:Record.Name~}</span>` },
		{ Hash: 'Detail-CraftOpt', Template: /*html*/`<option value="{~D:Record.Value~}" {~D:Record.SelectedAttr~}>{~D:Record.Label~}</option>` },
		{
			Hash: 'Detail-WorkItemPrompts',
			Template: /*html*/`
<div class="ps-detail-comments-wrap">
	<h4 class="ps-detail-comments-h">{~D:Record.Label~}</h4>
	<div id="{~D:Record.ContainerID~}"></div>
</div>`
		},
		{
			Hash: 'Detail-Prompts',
			Template: /*html*/`
<div class="ps-detail-comments-wrap">
	<h4 class="ps-detail-comments-h">{~D:Record.Label~}</h4>
	<div id="{~D:Record.ContainerID~}"></div>
</div>`
		},
		{ Hash: 'Detail-Field', Template: /*html*/`<div class="ps-detail-fcell {~D:Record.WideClass~}"><div class="ps-detail-flabel">{~D:Record.L~}</div><div class="ps-detail-fval">{~D:Record.Value~}{~TS:Detail-FieldMD:Record.MDSlot~}</div></div>` },
		{ Hash: 'Detail-FieldMD', Template: /*html*/`<div class="ps-detail-fmd" id="{~D:Record.ContainerID~}"></div>` },
		{ Hash: 'Detail-HeaderAction', Template: /*html*/`<button class="ps-btn ps-btn-primary" onclick="_Pict.views['Plansheet-EntityDetail'].headerAction('{~D:Record.Method~}')">{~D:Record.Label~}</button>` },
		{ Hash: 'Detail-AddressToggle', Template: /*html*/`<button class="ps-btn" onclick="_Pict.views['Plansheet-EntityDetail'].toggleAddressed()">{~D:Record.Label~}</button>` },
		{ Hash: 'Detail-Delete', Template: /*html*/`<button class="ps-btn ps-btn-danger" onclick="_Pict.views['Plansheet-EntityDetail'].deleteRecord()">Delete</button>` },
		{ Hash: 'Detail-AddressedBadge', Template: /*html*/`<span class="ps-addressed-badge">{~D:Record.Label~}</span>` },
		{
			Hash: 'Detail-Steps',
			Template: /*html*/`
<div class="ps-detail-steps-wrap">
	<h4 class="ps-detail-board-h">{~D:Record.Label~}</h4>
	<div id="{~D:Record.ContainerID~}"></div>
</div>`
		},
		{
			Hash: 'Detail-Section',
			Template: /*html*/`
<div class="ps-rel">
	<h4>{~D:Record.Label~}</h4>
	{~TS:Detail-SubVisions:Record.SubVisionSlot~}
	{~TS:Detail-Fk:Record.FkSlot~}
	{~TS:Detail-Link:Record.LinkSlot~}
	{~TS:Detail-Inverse:Record.InverseSlot~}
	{~TS:Detail-Target:Record.TargetSlot~}
	{~TS:Detail-Sessions:Record.SessionsSlot~}
	{~TS:Detail-Media:Record.MediaSlot~}
	{~TS:Detail-Docs:Record.DocsSlot~}
	{~TS:Detail-DocLink:Record.DocLinkSlot~}
</div>`
		},
		// A host div, not a <select> -- _mountRelationPickers() mounts a server-backed candidate search
		// picker into it after render (WI #446).
		{ Hash: 'Detail-Fk', Template: /*html*/`<div class="ps-input-picker" id="{~D:Record.ContainerID~}"></div>` },
		{
			Hash: 'Detail-Link',
			Template: /*html*/`
<div class="ps-rel-items">{~TS:Detail-LinkItem:Record.Items~}{~TS:Detail-LinkEmpty:Record.EmptySlot~}</div>
{~TS:Detail-LinkAdd:Record.AddSlot~}`
		},
		{ Hash: 'Detail-LinkItem', Template: /*html*/`<span class="ps-chip"><a class="ps-link" onclick="_Pict.views['Plansheet-EntityDetail'].openTarget('{~D:Record.TargetKey~}',{~D:Record.TargetID~})">{~D:Record.Label~}</a><span class="ps-chip-rel">{~D:Record.RelType~}</span><button class="ps-chip-x" title="Unlink" onclick="_Pict.views['Plansheet-EntityDetail'].removeLink({~D:Record.Idx~},{~D:Record.JoinID~})">×</button></span>` },
		{ Hash: 'Detail-LinkEmpty', Template: /*html*/`<span class="ps-rel-empty">None linked yet.</span>` },
		{
			Hash: 'Detail-LinkAdd',
			Template: /*html*/`
<div class="ps-rel-add">
	<div class="ps-input-picker ps-rel-picker" id="{~D:Record.ContainerID~}"></div>
	{~TS:Detail-RelType:Record.RelTypeSlot~}
	<button class="ps-btn ps-btn-primary" onclick="_Pict.views['Plansheet-EntityDetail'].addLink({~D:Record.Idx~})">+ Link</button>
</div>`
		},
		{ Hash: 'Detail-Opt2', Template: /*html*/`<option value="{~D:Record.Value~}">{~D:Record.Label~}</option>` },
		{ Hash: 'Detail-RelType', Template: /*html*/`<select class="ps-input" onchange="_Pict.views['Plansheet-EntityDetail'].setPendingRelType({~D:Record.Idx~}, this.value)">{~TS:Detail-RelTypeOpt:Record.Options~}</select>` },
		{ Hash: 'Detail-RelTypeOpt', Template: /*html*/`<option value="{~D:Record.Value~}">{~D:Record.Value~}</option>` },
		{
			Hash: 'Detail-Inverse',
			Template: /*html*/`<div class="ps-rel-items">{~TS:Detail-InvItem:Record.Items~}{~TS:Detail-InvEmpty:Record.EmptySlot~}</div>{~TS:Detail-InvCreate:Record.CreateSlot~}`
		},
		{ Hash: 'Detail-InvCreate', Template: /*html*/`<div class="ps-rel-add"><button class="ps-btn ps-btn-primary" onclick="_Pict.views['Plansheet-EntityDetail'].createFromRelation({~D:Record.Idx~})">+ {~D:Record.Label~}</button></div>` },
		{ Hash: 'Detail-InvItem', Template: /*html*/`<span class="ps-chip"><a class="ps-link" onclick="_Pict.views['Plansheet-EntityDetail'].openTarget('{~D:Record.TargetKey~}',{~D:Record.TargetID~})">{~D:Record.Label~}</a><span class="ps-chip-sub">{~D:Record.Sub~}</span></span>` },
		{ Hash: 'Detail-InvEmpty', Template: /*html*/`<span class="ps-rel-empty">None yet.</span>` },
		{
			Hash: 'Detail-Target',
			Template: /*html*/`
<div class="ps-rel-target">
	<select class="ps-input" onchange="_Pict.views['Plansheet-EntityDetail'].setTargetType(this.value)">{~TS:Detail-TargetTypeOpt:Record.TypeOptions~}</select>
	{~TS:Detail-TargetEntity:Record.EntitySlot~}
</div>`
		},
		{ Hash: 'Detail-TargetTypeOpt', Template: /*html*/`<option value="{~D:Record.Value~}" {~D:Record.SelectedAttr~}>{~D:Record.Value~}</option>` },
		// A host div, not a <select> -- see Detail-Fk above.
		{ Hash: 'Detail-TargetEntity', Template: /*html*/`<div class="ps-input-picker ps-rel-picker" id="{~D:Record.ContainerID~}"></div>` },
		{
			Hash: 'Detail-Sessions',
			Template: /*html*/`
<div class="ps-rel-items">{~TS:Detail-SessionItem:Record.Items~}{~TS:Detail-LinkEmpty:Record.EmptySlot~}</div>
{~TS:Detail-SessionAdd:Record.AddSlot~}`
		},
		{ Hash: 'Detail-SessionItem', Template: /*html*/`<span class="ps-chip"><span class="ps-chip-title">{~D:Record.Title~}</span><select class="ps-input ps-input-sm" title="Outcome" onchange="_Pict.views['Plansheet-EntityDetail'].setSessionOutcome({~D:Record.ID~}, this.value)">{~TS:Detail-TargetTypeOpt:Record.OutcomeOptions~}</select><button class="ps-chip-x" title="Unlink" onclick="_Pict.views['Plansheet-EntityDetail'].unlinkSession('{~D:Record.Field~}',{~D:Record.ID~})">×</button></span>` },
		{
			Hash: 'Detail-SessionAdd',
			Template: /*html*/`<div class="ps-rel-add"><select class="ps-input" onchange="_Pict.views['Plansheet-EntityDetail'].setPendingSession({~D:Record.Idx~}, this.value)">{~TS:Detail-Opt2:Record.Options~}</select><button class="ps-btn ps-btn-primary" onclick="_Pict.views['Plansheet-EntityDetail'].linkSession({~D:Record.Idx~})">+ Link session</button></div>`
		},
		{
			Hash: 'Detail-Docs',
			Template: /*html*/`
<div class="ps-rel-items">{~TS:Detail-DocItem:Record.Items~}{~TS:Detail-DocEmpty:Record.EmptySlot~}</div>
<div class="ps-rel-add"><button class="ps-btn ps-btn-primary" onclick="_Pict.views['Plansheet-EntityDetail'].newDoc()">+ New document</button></div>`
		},
		{ Hash: 'Detail-DocItem', Template: /*html*/`<span class="ps-chip"><a class="ps-link" onclick="_Pict.views['Plansheet-EntityDetail'].openDoc('{~D:Record.Path~}')">{~D:Record.Title~}</a><button class="ps-chip-x" title="Delete" onclick="_Pict.views['Plansheet-EntityDetail'].removeDoc({~D:Record.JoinID~},'{~D:Record.Path~}')">×</button></span>` },
		{ Hash: 'Detail-DocEmpty', Template: /*html*/`<span class="ps-rel-empty">No documents yet.</span>` },
		{
			Hash: 'Detail-CoreDoc',
			Template: /*html*/`
<div class="ps-detail-loops-wrap" style="margin:18px 0;">
	<h4 style="font-size:14px;font-weight:700;color:var(--theme-color-text-primary,#2c3140);margin:0 0 10px;">{~D:Record.Label~}</h4>
	<div class="ps-coredoc">{~TS:Detail-CoreDocBody:Record.HasBody~}{~TS:Detail-CoreDocEmpty:Record.EmptySlot~}
		<div class="ps-rel-add"><button class="ps-btn ps-btn-primary" onclick="_Pict.views['Plansheet-EntityDetail'].openCoreDoc()">Edit vision document</button></div>
	</div>
</div>`
		},
		{ Hash: 'Detail-CoreDocBody', Template: /*html*/`<div class="ps-coredoc-body" id="EntityDetail-CoreDoc"></div>` },
		{ Hash: 'Detail-CoreDocEmpty', Template: /*html*/`<div class="ps-coredoc-empty">No core document yet — open the editor to start. Drop in prose, images, diagrams, KaTeX, and Excalidraw drawings.</div>` },
		{
			Hash: 'Detail-SubVisions',
			Template: /*html*/`
<div class="ps-subvision">
	<div class="ps-subv-grid">{~TS:Detail-SubVisionCard:Record.Children~}{~TS:Detail-SubVisionEmpty:Record.ChildEmpty~}</div>
	{~TS:Detail-SubVisionParents:Record.ParentSlot~}
	<div class="ps-rel-add">
		<input class="ps-input" type="text" placeholder="New sub-vision name…" onkeydown="if(event.key==='Enter'){event.preventDefault();_Pict.views['Plansheet-EntityDetail'].createSubVision({~D:Record.Idx~});}" oninput="_Pict.views['Plansheet-EntityDetail'].setPendingSubName({~D:Record.Idx~}, this.value)">
		<button class="ps-btn ps-btn-primary" onclick="_Pict.views['Plansheet-EntityDetail'].createSubVision({~D:Record.Idx~})">+ Create sub-vision</button>
	</div>
	{~TS:Detail-SubVisionLink:Record.AddSlot~}
</div>`
		},
		{ Hash: 'Detail-SubVisionCard', Template: /*html*/`<div class="ps-subv-card" onclick="_Pict.views['Plansheet-EntityDetail'].openTarget('Vision',{~D:Record.TargetID~})"><div class="ps-subv-card-head"><span class="ps-subv-num">#{~D:Record.Number~}</span><button class="ps-chip-x" title="Remove part" onclick="event.stopPropagation();_Pict.views['Plansheet-EntityDetail'].removeSubVision({~D:Record.Idx~},{~D:Record.JoinID~})">×</button></div><div class="ps-subv-name">{~D:Record.Name~}</div><div class="ps-subv-sub">{~D:Record.Status~}</div></div>` },
		{ Hash: 'Detail-SubVisionEmpty', Template: /*html*/`<span class="ps-rel-empty">No parts yet — decompose this vision into smaller, aligned sub-visions.</span>` },
		{ Hash: 'Detail-SubVisionParents', Template: /*html*/`<div class="ps-subv-partof"><span class="ps-subv-partof-label">Part of:</span>{~TS:Detail-SubVisionParentChip:Record.Items~}</div>` },
		{ Hash: 'Detail-SubVisionParentChip', Template: /*html*/`<span class="ps-chip"><a class="ps-link" onclick="_Pict.views['Plansheet-EntityDetail'].openTarget('Vision',{~D:Record.TargetID~})">#{~D:Record.Number~} {~D:Record.Name~}</a></span>` },
		// A host div, not a <select> -- see Detail-Fk above.
		{ Hash: 'Detail-SubVisionLink', Template: /*html*/`<div class="ps-rel-add"><div class="ps-input-picker ps-rel-picker" id="{~D:Record.ContainerID~}"></div><button class="ps-btn" onclick="_Pict.views['Plansheet-EntityDetail'].addSubVision({~D:Record.Idx~})">+ Add existing as part</button></div>` },
		{
			Hash: 'Detail-Media',
			Template: /*html*/`
<div class="ps-media-grid">{~TS:Detail-MediaItem:Record.Items~}{~TS:Detail-MediaEmpty:Record.EmptySlot~}</div>
<div class="ps-rel-add"><label class="ps-btn ps-btn-primary ps-file-btn">+ Upload<input type="file" style="display:none" onchange="_Pict.views['Plansheet-EntityDetail'].uploadMedia({~D:Record.Idx~}, this.files[0])"></label></div>`
		},
		{ Hash: 'Detail-MediaItem', Template: /*html*/`<div class="ps-media-item">{~TS:Detail-MediaImage:Record.ImageSlot~}{~TS:Detail-MediaVideo:Record.VideoSlot~}{~TS:Detail-MediaFile:Record.FileSlot~}<button class="ps-chip-x ps-media-x" title="Remove" onclick="_Pict.views['Plansheet-EntityDetail'].removeMedia({~D:Record.ID~})">×</button></div>` },
		{ Hash: 'Detail-MediaImage', Template: /*html*/`<img class="ps-media-thumb ps-media-view" src="{~D:Record.URL~}" alt="{~D:Record.Title~}" title="Click to view - {~D:Record.Title~}" data-url="{~D:Record.URL~}" data-title="{~D:Record.Title~}" style="cursor:zoom-in;" onclick="_Pict.PictApplication.viewImage(this.dataset.url, this.dataset.title)">` },
		{ Hash: 'Detail-MediaVideo', Template: /*html*/`<video class="ps-media-video" controls preload="metadata" src="{~D:Record.URL~}" title="{~D:Record.Title~}"></video>` },
		{ Hash: 'Detail-MediaFile', Template: /*html*/`<a class="ps-link ps-media-file" href="{~D:Record.URL~}" target="_blank" rel="noopener"><span class="ps-media-file-type">{~D:Record.Type~}</span>{~D:Record.Title~}</a>` },
		{ Hash: 'Detail-MediaEmpty', Template: /*html*/`<span class="ps-rel-empty">No media yet.</span>` },
		{
			Hash: 'Detail-DocLink',
			Template: /*html*/`
<div class="ps-rel-items">{~TS:Detail-DocLinkItem:Record.Items~}{~TS:Detail-DocLinkEmpty:Record.EmptySlot~}</div>
{~TS:Detail-DocLinkAdd:Record.AddSlot~}`
		},
		{ Hash: 'Detail-DocLinkItem', Template: /*html*/`<span class="ps-chip"><a class="ps-link" onclick="_Pict.views['Plansheet-EntityDetail'].openFoundationDoc('{~D:Record.Path~}')">{~D:Record.Title~}</a><button class="ps-chip-x" title="Unlink" onclick="_Pict.views['Plansheet-EntityDetail'].removeDocLink({~D:Record.Idx~},{~D:Record.JoinID~})">×</button></span>` },
		{ Hash: 'Detail-DocLinkEmpty', Template: /*html*/`<span class="ps-rel-empty">No documentation linked yet.</span>` },
		{
			Hash: 'Detail-DocLinkAdd',
			Template: /*html*/`<div class="ps-rel-add"><select class="ps-input" onchange="_Pict.views['Plansheet-EntityDetail'].setPendingDocLink({~D:Record.Idx~}, this.value)">{~TS:Detail-Opt2:Record.Options~}</select><button class="ps-btn ps-btn-primary" onclick="_Pict.views['Plansheet-EntityDetail'].addDocLink({~D:Record.Idx~})">+ Link doc</button></div>`
		}
	],
	Renderables:
	[
		{
			RenderableHash: 'EntityDetail-Display',
			TemplateHash: 'EntityDetail-Display',
			ContentDestinationAddress: '#Plansheet-Workspace'
		}
	]
};
